/*
 * Design Studio web component
 * Steps: Dimensions (cm) → Design (browse/AI) → Room preview → Review/Cart.
 * Vanilla custom elements. Backend via /apps/ai/* (Shopify App Proxy).
 */
(function () {
  'use strict';

  const STEPS = ['Design', 'Dimensions', 'Room', 'Review'];

  /**
   * Quad-to-quad projective transform (Heckbert).
   * Maps the rectangle (0,0)-(W,H) onto destination quad p1..p4 (TL,TR,BR,BL in CSS px).
   * Returns a CSS `matrix3d(...)` string. Apply with `transform-origin: 0 0`.
   */
  function quadToQuadMatrix3d(W, H, p1, p2, p3, p4) {
    // Map unit square (0,0),(1,0),(1,1),(0,1) -> p1,p2,p3,p4
    const dx1 = p2.x - p3.x, dy1 = p2.y - p3.y;
    const dx2 = p4.x - p3.x, dy2 = p4.y - p3.y;
    const sx = p1.x - p2.x + p3.x - p4.x;
    const sy = p1.y - p2.y + p3.y - p4.y;
    let a, b, c, d, e, f, g, h;
    const det = dx1 * dy2 - dy1 * dx2;
    if (Math.abs(sx) < 1e-6 && Math.abs(sy) < 1e-6) {
      a = p2.x - p1.x; b = p3.x - p2.x; c = p1.x;
      d = p2.y - p1.y; e = p3.y - p2.y; f = p1.y;
      g = 0; h = 0;
    } else if (Math.abs(det) < 1e-6) {
      return 'none';
    } else {
      g = (sx * dy2 - sy * dx2) / det;
      h = (dx1 * sy - dy1 * sx) / det;
      a = p2.x - p1.x + g * p2.x;
      b = p4.x - p1.x + h * p4.x;
      c = p1.x;
      d = p2.y - p1.y + g * p2.y;
      e = p4.y - p1.y + h * p4.y;
      f = p1.y;
    }
    // Scale unit-square mapping to (W,H) source size.
    a /= W; d /= W; g /= W;
    b /= H; e /= H; h /= H;
    return `matrix3d(${a},${d},0,${g}, ${b},${e},0,${h}, 0,0,1,0, ${c},${f},0,1)`;
  }

  class DesignStudio extends HTMLElement {
    connectedCallback() {
      const params = new URLSearchParams(window.location.search);
      const urlPrompt = (params.get('prompt') || '').trim();
      const urlDesignUrl = (params.get('designUrl') || '').trim();
      const urlDesignTitle = (params.get('title') || '').trim();
      const urlDesignHandle = (params.get('handle') || '').trim();
      const urlStepRaw = (params.get('step') || '').trim().toLowerCase();
      const urlW = Number(params.get('w') || 0);
      const urlH = Number(params.get('h') || 0);
      const stepAliases = { design: 1, dimensions: 2, room: 3, preview: 3, review: 4, cart: 4 };
      let urlStep = Number(urlStepRaw);
      if (!Number.isFinite(urlStep) || urlStep < 1 || urlStep > 4) {
        urlStep = stepAliases[urlStepRaw] || 0;
      }
      const wastageDefault = Number(this.dataset.wastageDefault || 10);
      // Merchant can hide the wastage option entirely from the theme editor.
      // When off, no wastage is shown or added to the price.
      const enableWastage = this.dataset.enableWastage !== 'false';
      const preselected = urlDesignUrl ? [{
        id: urlDesignHandle ? `col:${urlDesignHandle}` : `pdp:${urlDesignUrl}`,
        imageUrl: urlDesignUrl,
        source: 'collection',
        title: urlDesignTitle || 'Selected wallpaper',
      }] : [];
      const initialDims = {
        width: urlW >= 10 ? urlW : 300,
        height: urlH >= 10 ? urlH : 250,
        repeat: 0,
        wastage: wastageDefault,
      };
      let initialStep = preselected.length ? 3 : (urlPrompt ? 2 : 1);
      if (urlStep) initialStep = urlStep;
      if (initialStep >= 3 && preselected.length === 0) initialStep = urlPrompt ? 2 : 1;
      this.state = {
        step: initialStep,
        knowsDims: true,
        wastageOn: enableWastage && wastageDefault > 0,
        wastageDefault,
        wastageEnabled: enableWastage,
        // All wall dimensions in CENTIMETRES.
        dims: initialDims,
        sourceTab: urlPrompt ? 'ai' : null,
        chosen: preselected,
        roomSource: 'preset',
        roomUrl: null,
        roomPublicUrl: null,
        roomFitUrl: null,
        roomFitWallpaperUrl: null,
        roomFitLoading: false,
        roomFitStage: null,
        roomFitError: null,
        roomFitAspectRatio: null,
        roomCompare: 50,
        roomFitLimit: 5,
        roomFitRemaining: 5,
        roomFitUsageLoading: false,
        previewUrl: null,
        aiJobId: null,
        aiOutputs: [],
        aiOutputPrompt: '',
        aiHistory: [],
        aiPrompt: urlPrompt,
        aiLoading: false,
        aiError: null,
        // Crop rectangle as % of design image natural size; aspect locked to widthCm:heightCm.
        crop: null,
        flip: false,
        // Image transform inside the fixed crop frame (Review step).
        imgTransform: { tx: 0, ty: 0, scale: 1 },
        bwMode: false,
        // Hanging instructions add-on (Review step).
        posterInstructions: false,
        // AI usage counter — capped by aiGenerationLimit.
        aiGenerationsUsed: 0,
        // Upload-your-own (Step 1) state.
        uploadLoading: false,
        uploadError: null,
        uploadPreviewUrl: null,
        uploadPublicUrl: null,
        uploadFileName: null,
        uploadFileSize: null,
        uploadDims: null,
      };
      this.proxy = (this.dataset.proxyBase || '/apps/ai').replace(/\/$/, '');
      this.customerId = (this.dataset.customerId || '').trim();
      const loginBase = this.dataset.loginUrl || '/account/login';
      const returnUrl = `${window.location.pathname}${window.location.search}`;
      this.loginUrl = `${loginBase}${loginBase.includes('?') ? '&' : '?'}return_url=${encodeURIComponent(returnUrl)}`;
      this.currency = this.dataset.currency || 'AUD';
      this.rollWidthCm = Number(this.dataset.rollWidthCm || 53);
      this.rollLengthCm = Number(this.dataset.rollLengthM || 10) * 100;
      this.pricePerRoll = Number(this.dataset.pricePerRoll || 89);
      this.pricePerM2 = Number(this.dataset.pricePerM2 || 0);
      // Minimum ORDERABLE wall area in m². Walls below this cannot be ordered
      // at all — they are blocked, not rounded up to a floor price. Mirrors
      // PW_MIN_ORDER_M2 on the backend, which rejects them authoritatively.
      this.minOrderM2 = Math.max(0, Number(this.dataset.minOrderM2 || 1));
      this.aiVariantId = this.dataset.aiVariantId || '';
      this.aiVariantUnitPrice = Number(this.dataset.aiVariantUnitPrice || 1);
      this.surchargeVariantId = this.dataset.surchargeVariantId || this.aiVariantId || '';
      this.aiGenerationLimit = Math.max(1, Number(this.dataset.aiGenerationLimit || 3));
      this.aiUsageStorageKey = 'pw_ds_ai_usage_v1';
      this.aiUsageWindowMs = 24 * 60 * 60 * 1000;
      // Hydrate persisted usage so the counter survives reloads (rolls over after 24h).
      const persistedUsage = this.readPersistedAiUsage();
      this.state.aiGenerationsUsed = persistedUsage;
      this.aiHistoryStorageKey = 'pw_ds_ai_history_v1';
      this.aiHistoryMax = 24;
      this.state.aiHistory = this.readAiHistory();
      this.posterInstructionsLabel = this.dataset.posterInstructionsLabel || 'Include hanging instructions';
      this.posterInstructionsTooltip = this.dataset.posterInstructionsTooltip || '';
      this.leadTime = this.dataset.leadTime || '';
      // Room presets from section blocks (image + label). Falls back to a small Unsplash set.
      const fallbackByLabel = {
        'living room':  'https://images.unsplash.com/photo-1616627561950-9f746e330187?auto=format&fit=crop&w=900&q=70',
        'bedroom':      'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=900&q=70',
        'kids room':    'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=900&q=70',
        'nursery':      'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=900&q=70',
        'hallway':      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=900&q=70',
        'feature wall': 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=900&q=70',
        'office':       'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=70',
        'dining':       'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?auto=format&fit=crop&w=900&q=70',
      };
      const defaultFallback = fallbackByLabel['living room'];
      const fallbackPresets = [
        { label: 'Living room',  url: fallbackByLabel['living room'] },
        { label: 'Bedroom',      url: fallbackByLabel['bedroom'] },
        { label: 'Hallway',      url: fallbackByLabel['hallway'] },
        { label: 'Feature wall', url: fallbackByLabel['feature wall'] },
      ];
      let presets = [];
      try { presets = JSON.parse(this.dataset.roomPresets || '[]'); } catch { presets = []; }
      // Fill missing url per preset using label-keyed Unsplash fallbacks.
      presets = (Array.isArray(presets) ? presets : []).map((p) => {
        if (p && (!p.url || !String(p.url).trim())) {
          const key = String(p.label || '').trim().toLowerCase();
          return { ...p, url: fallbackByLabel[key] || defaultFallback };
        }
        return p;
      });
      this.roomPresets = presets.length ? presets : fallbackPresets;
      this.addToCartUrl = this.dataset.addToCartUrl || '/cart/add.js';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.contactUrl = this.dataset.contactUrl || '/pages/contact';
      this.sharedProductHandle = 'pickawall-custom-wallpaper';
      this.uploadMaxMb = Math.max(1, Number(this.dataset.uploadMaxMb || 20));
      this.browseColumns = Math.min(5, Math.max(2, Number(this.dataset.browseColumns || 4)));
      this.minUploadWidth = Math.max(0, Number(this.dataset.minUploadWidth || 2000));
      this.excludedTags = String(this.dataset.excludedTags || '').split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
      // Room preview: merchant can hide the "upload your own room" option.
      this.enableRoomUpload = this.dataset.enableRoomUpload !== 'false';
      // AI model (admin-selected). Forwarded to /generate; pro models yield 1
      // design per generation, schnell yields 4 (drives the button/help labels).
      this.aiModel = (this.dataset.aiModel || '').trim();
      this.aiDesignsPerGen = Math.max(1, Number(this.dataset.aiDesignsPerGen || 4));
      // Admin prompt-shaping instructions read from the pickawall_ai_instructions
      // metaobject in Liquid (storefront context). Forwarded to /generate as a
      // FALLBACK the backend uses only when its own server-side read is empty
      // (the app token may lack the read_metaobjects scope).
      this.aiInstructions = {
        systemPrompt: (this.dataset.aiSystemPrompt || '').trim(),
        styleSuffix: (this.dataset.aiStyleSuffix || '').trim(),
        negativePrompt: (this.dataset.aiNegativePrompt || '').trim(),
      };
      // Source cards (Step 1 entry tiles) — admin-editable via section settings.
      const defaultCards = [
        { id: 'browse', title: 'Browse our wallpaper inspiration', desc: 'Explore thousands of curated designs.', cta: 'Browse Designs', image: '' },
        { id: 'ai', title: 'Design with AI', desc: 'Describe the wallpaper you imagine and let AI create it.', cta: 'Start Designing', image: '' },
        { id: 'upload', title: 'Upload your own', desc: 'Bring your favourite photo or artwork — we’ll make it wallpaper-ready.', cta: 'Upload Image', image: '' },
      ];
      let cards = [];
      try { cards = JSON.parse(this.dataset.sourceCards || '[]'); } catch { cards = []; }
      // Keep only merchant-enabled cards (enabled !== false). Fall back to the
      // hardcoded defaults only if parsing failed / nothing came through.
      this.sourceCards = Array.isArray(cards) ? cards.filter((c) => c && c.enabled !== false) : [];
      if (!this.sourceCards.length) this.sourceCards = defaultCards;
      // If a disabled source was pre-selected (e.g. ?prompt forces the AI tab
      // but AI is turned off), fall back to the chooser.
      if (this.state.sourceTab && !this.sourceCards.some((c) => c.id === this.state.sourceTab)) {
        this.state.sourceTab = null;
      }
      this.render();
      if (this.customerId) this.loadAiHistoryFromServer();
      if (this.customerId && this.enableRoomUpload) this.loadRoomPreviewUsage();
    }

    setState(patch) {
      const stepChanged = Object.prototype.hasOwnProperty.call(patch, 'step') && patch.step !== this.state.step;
      const previousWallpaper = this.state.chosen[0]?.imageUrl || '';
      const nextState = { ...this.state, ...patch };
      const nextWallpaper = nextState.chosen[0]?.imageUrl || '';
      const designChanged = previousWallpaper !== nextWallpaper;
      const enteringRoom = this.state.step !== 3 && nextState.step === 3;
      if (designChanged && nextState.roomSource === 'upload') {
        nextState.roomFitUrl = null;
        nextState.roomFitError = null;
        nextState.roomFitWallpaperUrl = null;
      }
      const shouldRegenerateRoom = (designChanged || enteringRoom)
        && nextState.step === 3
        && nextState.roomSource === 'upload'
        && nextState.roomPublicUrl
        && nextWallpaper
        && !nextState.roomFitLoading
        && nextState.roomFitWallpaperUrl !== this.absoluteUrl(nextWallpaper);
      this.state = nextState;
      this.render();
      if (stepChanged) this.scrollToTop();
      if (shouldRegenerateRoom) {
        queueMicrotask(() => this.fitUploadedRoomUrl(this.state.roomPublicUrl));
      }
    }

    /** Bring the studio section back to the top of the viewport after step changes. */
    scrollToTop() {
      // Prefer the shell wrapper (includes heading); fall back to the component.
      const target = this.closest('.ds-shell') || this;
      const rect = target.getBoundingClientRect();
      // Offset for any sticky site header (~72px is a safe average).
      const top = window.scrollY + rect.top - 72;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    /** Roll calc — all inputs in cm. */
    calc() {
      const { width, height } = this.state.dims;
      const wastagePct = this.state.wastageOn ? (Number(this.state.dims.wastage) || 0) : 0;
      const wallAreaM2 = (width / 100) * (height / 100);
      if (!this.state.knowsDims || !width || !height) {
        return { wallAreaM2: 0, billedM2: 0, total: 0, rolls: 0, wastagePct: 0, belowMinimum: false };
      }
      // No floor is applied to the price: a wall below the minimum simply
      // cannot be ordered (see belowMinimum), so it never reaches the cart.
      // The threshold is checked against the MEASURED area, not the area plus
      // wastage — wastage is an allowance we add for trimming, and must not be
      // what lifts an undersized wall over the line.
      const billedM2 = wallAreaM2 * (1 + wastagePct / 100);
      const belowMinimum = wallAreaM2 < this.minOrderM2 - 1e-9;
      const total = billedM2 * (this.pricePerM2 || 0);
      // Rolls kept only as an internal shipping-unit reference.
      const stripsNeeded = Math.ceil(width / this.rollWidthCm);
      const stripLength = height;
      const stripsPerRoll = Math.max(1, Math.floor(this.rollLengthCm / stripLength));
      const rolls = Math.ceil(stripsNeeded / stripsPerRoll);
      return { wallAreaM2, billedM2, rolls, total, wastagePct, belowMinimum };
    }

    money(n) {
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(n); }
      catch { return `$${n.toFixed(2)}`; }
    }

    formatBytes(bytes) {
      const b = Number(bytes) || 0;
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
      return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    }

    measureImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = reject;
        img.src = src;
      });
    }

    closestRoomAspect(width, height) {
      const ratios = [
        ['1:1', 1], ['2:3', 2 / 3], ['3:2', 3 / 2], ['3:4', 3 / 4],
        ['4:3', 4 / 3], ['4:5', 4 / 5], ['5:4', 5 / 4], ['9:16', 9 / 16],
        ['16:9', 16 / 9], ['21:9', 21 / 9],
      ];
      const source = Math.max(1, width) / Math.max(1, height);
      return ratios.reduce((best, candidate) =>
        Math.abs(Math.log(candidate[1] / source)) < Math.abs(Math.log(best[1] / source)) ? candidate : best
      )[0];
    }

    render() {
      this.innerHTML = `
        ${this.renderStepper()}
        <div class="ds-stage">${this.renderStep()}</div>
        <aside class="ds-summary">${this.renderSummary()}</aside>
        ${this.renderModal()}
        ${this.renderCartOverlay()}
        ${this.renderRoomFitOverlay()}
      `;
      this.querySelectorAll('[data-action]').forEach((el) => el.addEventListener('click', (e) => this.onAction(e)));
      this.querySelectorAll('[data-stop]').forEach((el) => el.addEventListener('click', (e) => e.stopPropagation()));
      this.querySelectorAll('[data-bind]').forEach((el) => el.addEventListener('input', (e) => this.onBind(e)));
      const file = this.querySelector('[data-room-upload]');
      if (file) {
        file.addEventListener('click', (e) => {
          if (!this.customerId) e.preventDefault();
        });
        file.addEventListener('change', (e) => this.onRoomUpload(e));
      }
      const roomCompare = this.querySelector('[data-room-compare]');
      if (roomCompare) {
        roomCompare.addEventListener('pointerdown', (e) => this.onRoomComparePointerDown(e));
        roomCompare.addEventListener('keydown', (e) => this.onRoomCompareKeyDown(e));
      }
      const designFile = this.querySelector('[data-design-upload]');
      if (designFile) designFile.addEventListener('change', (e) => this.onDesignUpload(e));
      const uploadDrop = this.querySelector('[data-upload-drop]');
      if (uploadDrop) {
        ['dragenter', 'dragover'].forEach((ev) => uploadDrop.addEventListener(ev, (e) => {
          e.preventDefault();
          uploadDrop.classList.add('is-dragover');
        }));
        ['dragleave', 'dragend'].forEach((ev) => uploadDrop.addEventListener(ev, () => uploadDrop.classList.remove('is-dragover')));
        uploadDrop.addEventListener('drop', (e) => {
          e.preventDefault();
          uploadDrop.classList.remove('is-dragover');
          const dropped = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (dropped) this.onDesignUpload({ target: { files: [dropped] } });
        });
      }
      const searchInput = this.querySelector('[data-browse-search-input]');
      if (searchInput) searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.state.collection = null;
          this.fetchCollection({ reset: true });
          this.render();
        }
      });

      // Room preview interactions
      const handles = this.querySelectorAll('[data-handles] .ds-handle');
      handles.forEach((h) => h.addEventListener('pointerdown', (e) => this.onHandlePointerDown(e)));

      // Crop step interactions (legacy — kept harmless if elements absent)
      this.querySelectorAll('[data-crop-handle]').forEach((h) =>
        h.addEventListener('pointerdown', (e) => this.onCropHandle(e))
      );
      const cropRect = this.querySelector('[data-crop-rect]');
      if (cropRect) cropRect.addEventListener('pointerdown', (e) => this.onCropDrag(e));
      const cropImg = this.querySelector('[data-crop-img]');
      if (cropImg) {
        const onLoad = () => this.clampCrop();
        if (cropImg.complete) onLoad(); else cropImg.addEventListener('load', onLoad, { once: true });
      }

      // Review step: pan + zoom the wallpaper inside a fixed-size canvas.
      const finalImg = this.querySelector('[data-final-img]');
      if (finalImg) {
        finalImg.addEventListener('pointerdown', (e) => this.onFinalImgDrag(e));
        const onLoad = () => this.fitFinalImage();
        if (finalImg.complete && finalImg.naturalWidth) onLoad();
        else finalImg.addEventListener('load', onLoad, { once: true });
      }
      const finalCanvas = this.querySelector('[data-final-canvas]');
      if (finalCanvas) finalCanvas.addEventListener('pointerdown', (e) => {
        // Don't double-bind when starting drag on the image itself.
        if (e.target === finalImg) return;
        // Let overlay controls (flip FAB, etc.) receive their own clicks.
        if (e.target.closest('button')) return;
        this.onFinalImgDrag(e);
      });

      const roomImg = this.querySelector('[data-room-img]');
      if (roomImg) {
        const apply = () => this.applyPerspective();
        if (roomImg.complete) apply(); else roomImg.addEventListener('load', apply, { once: true });
        if (!this._resizeBound) {
          this._resizeBound = true;
          window.addEventListener('resize', () => this.applyPerspective());
        } else {
          requestAnimationFrame(apply);
        }
      }
    }

    renderStepper() {
      return `<ol class="ds-stepper" aria-label="Steps">
        ${STEPS.map((label, i) => {
          const n = i + 1;
          const isActive = this.state.step === n;
          const isDone = this.state.step > n;
          const cls = isActive ? 'is-active' : isDone ? 'is-done' : '';
          const clickable = isDone || isActive;
          const tag = clickable ? 'button' : 'span';
          const attrs = clickable
            ? `type="button" data-action="goto-step" data-step="${n}" aria-current="${isActive ? 'step' : 'false'}"`
            : `aria-disabled="true"`;
          return `<li class="ds-step ${cls}">
            <${tag} class="ds-step__btn" ${attrs}>
              <span class="ds-step__num" aria-hidden="true">${isDone ? '✓' : n}</span>
              <span class="ds-step__label">${label}</span>
            </${tag}>
          </li>`;
        }).join('')}
      </ol>`;
    }

    renderStep() {
      switch (this.state.step) {
        case 1: return this.tplDesign();
        case 2: return this.tplDimensions();
        case 3: return this.tplRoom();
        case 4: return this.tplReview();
        default: return '';
      }
    }

    stepHeading(num, text) {
      const n = String(num).padStart(2, '0');
      return `<header class="ds-step-heading"><span class="ds-step-heading__num">${n}.</span><h2 class="ds-step-heading__text h2">${text}</h2></header>`;
    }

    /* ---------------- Step 1: Dimensions (cm) ---------------- */
    tplDimensions() {
      const d = this.state.dims;
      const known = this.state.knowsDims;
      return `
        ${this.stepHeading(2, 'Your wall dimensions')}
        <div class="ds-panel">
          <h3 class="ds-panel__title h4">Wall Dimensions</h3>
          <div class="ds-choices">
            <button type="button" class="ds-choice ${known ? 'is-selected' : ''}" data-action="dims-mode" data-known="1">
              <span class="ds-choice__title">Yes, I know my dimensions</span>
              <span class="ds-choice__hint">Enter width & height</span>
            </button>
            <button type="button" class="ds-choice ${!known ? 'is-selected' : ''}" data-action="dims-mode" data-known="0">
              <span class="ds-choice__title">No, I'll measure later</span>
              <span class="ds-choice__hint">Add at checkout</span>
            </button>
          </div>
          <div class="ds-collapse" ${known ? '' : 'hidden'}>
            <div class="ds-fields">
              <label class="ds-field">
                <span class="ds-field__label">Width (cm)</span>
                <input class="ds-field__input" type="number" min="10" step="1" inputmode="numeric" placeholder="e.g. 300" data-bind="dims.width" value="${d.width || ''}">
              </label>
              <label class="ds-field">
                <span class="ds-field__label">Height (cm)</span>
                <input class="ds-field__input" type="number" min="10" step="1" inputmode="numeric" placeholder="e.g. 250" data-bind="dims.height" value="${d.height || ''}">
              </label>
            </div>
            ${this.renderMinOrderNotice()}
            ${this.state.wastageEnabled ? `
            <label class="ds-toggle">
              <input type="checkbox" data-action="toggle-wastage" ${this.state.wastageOn ? 'checked' : ''}>
              <span class="ds-toggle__label">
                <strong>Add ${this.state.wastageDefault}% extra for trimming &amp; offcuts</strong>
                <small>Recommended. Covers cuts around edges, outlets and pattern matching.</small>
              </span>
            </label>
            ` : ''}
          </div>
        </div>
        <div class="ds-actions">
          <button class="ds-btn button button--outline" data-action="back">Back</button>
          <button class="ds-btn button" data-action="next" ${this.canConfirmDims() ? '' : 'disabled'}>
            Next: preview in a room
          </button>
        </div>`;
    }

    canConfirmDims() {
      if (!this.state.knowsDims) return true; // "I'll measure later" can proceed
      const { width, height } = this.state.dims;
      if (!(Number(width) >= 10 && Number(height) >= 10)) return false;
      return !this.calc().belowMinimum;
    }

    /**
     * Minimum-order notice shown under the dimension inputs on both the
     * Dimensions step and the Review tab. Reads as a plain requirement until
     * the entered wall actually falls short, then becomes a blocking error.
     */
    renderMinOrderNotice() {
      return `<span data-min-order-slot>${this.minOrderNoticeInner()}</span>`;
    }

    minOrderNoticeInner() {
      if (!(this.minOrderM2 > 0)) return '';
      const min = this.minOrderM2.toFixed(2);
      const c = this.calc();
      if (!c.belowMinimum) {
        return `<p class="ds-field-hint">Minimum order ${min}&nbsp;m\u00b2.</p>`;
      }
      return `<p class="ds-field-error" role="alert">This wall is ${c.wallAreaM2.toFixed(2)}&nbsp;m\u00b2. The minimum order is ${min}&nbsp;m\u00b2 \u2014 please increase the width or height to continue.</p>`;
    }

    /* ---------------- Step 1: Design ---------------- */
    tplDesign() {
      const tab = this.state.sourceTab; // 'browse' | 'ai' | 'upload' | null
      const card = this.sourceCards.find((c) => c.id === tab);
      const chosenTitle = card ? card.title : '';
      return `
        ${this.stepHeading(1, 'Choose your wallpaper')}
        ${!tab ? `
          <div class="ds-hero-choices ds-hero-choices--three ds-hero-choices--n${this.sourceCards.length}" role="tablist">
            ${this.sourceCards.map((c) => `
              <button type="button" role="tab" class="ds-hero-choice ds-hero-choice--${c.id}" data-action="tab" data-tab="${c.id}">
                <span class="ds-hero-choice__icon ds-hero-choice__icon--${c.id}" aria-hidden="true">${this.heroIcon(c.id)}</span>
                <span class="ds-hero-choice__body">
                  <span class="ds-hero-choice__title">${this.escapeHtml(c.title)}</span>
                  <span class="ds-hero-choice__desc">${this.escapeHtml(c.desc)}</span>
                  <span class="ds-hero-choice__cta">${this.escapeHtml(c.cta)} <span aria-hidden="true">→</span></span>
                </span>
              </button>
            `).join('')}
          </div>` : `
          <div class="ds-source-pill">
            <span class="ds-source-pill__label">${this.escapeHtml(chosenTitle)}</span>
            <button type="button" class="ds-source-pill__change" data-action="tab" data-tab="">Change</button>
          </div>
          <div class="ds-tabpanel">
            ${tab === 'ai' ? this.tplAi() : tab === 'upload' ? this.tplUpload() : this.tplBrowse()}
          </div>
        `}
        <div class="ds-actions">
          <button class="ds-btn button button--outline" data-action="back" ${this.state.step <= 1 ? 'disabled' : ''}>Back</button>
          <button class="ds-btn button" data-action="next" ${this.state.chosen.length === 0 ? 'disabled' : ''}>Next: dimensions</button>
        </div>`;
    }

    escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /** Clean, consistent line icons for the three source-choice cards. */
    heroIcon(id) {
      const attrs = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" focusable="false"';
      if (id === 'ai') {
        return `<svg ${attrs}><path d="M11 3.5 12.7 8l4.5 1.7L12.7 11.4 11 16l-1.7-4.6L4.8 9.7 9.3 8z"/><path d="m18.5 14 .9 2.4 2.4.9-2.4.9-.9 2.4-.9-2.4-2.4-.9 2.4-.9z"/></svg>`;
      }
      if (id === 'upload') {
        return `<svg ${attrs}><path d="M12 15V4"/><path d="m7.5 8.5 4.5-4.5 4.5 4.5"/><path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15"/></svg>`;
      }
      // browse (default) — gallery / images
      return `<svg ${attrs}><rect x="3" y="4.5" width="14" height="12" rx="2"/><circle cx="8" cy="9" r="1.4"/><path d="m5 15 3.2-3.2a1.4 1.4 0 0 1 1.9 0L15 16.5"/><path d="M20 8.5v8A2.5 2.5 0 0 1 17.5 19H8"/></svg>`;
    }


    tplBrowse() {
      const c = this.state.collection;
      const q = this.state.collectionSearch || '';
      const searchBar = `
        <div class="ds-browse-search">
          <input type="search" class="ds-field__input" placeholder="Search designs by name…" value="${this.escapeHtml(q)}" data-bind="collectionSearch" data-browse-search-input>
          <button type="button" class="ds-btn button button--sm" data-action="browse-search">Search</button>
        </div>`;
      if (!c) {
        this.fetchCollection({ reset: true });
        return `${searchBar}<p style="color:var(--ds-muted);">Loading collection…</p>`;
      }
      if (c.products.length === 0) {
        return `${searchBar}<p style="color:var(--ds-muted);">No designs found${q ? ` for “${this.escapeHtml(q)}”` : ''}.</p>`;
      }
      const more = c.pageInfo && c.pageInfo.hasNextPage
        ? `<div class="ds-browse-more"><button type="button" class="ds-btn button button--outline" data-action="browse-more" ${this.state.collectionLoading ? 'disabled' : ''}>${this.state.collectionLoading ? 'Loading…' : 'Load more'}</button></div>`
        : '';
      return `${searchBar}
        <div class="ds-cards ds-cards--browse" style="--ds-browse-cols:${this.browseColumns}">
          ${c.products.map((p) => `
            <button class="ds-card ${this.isChosen('col:'+p.handle) ? 'is-chosen' : ''}" data-action="open-image" data-image="${p.featuredImage?.url ?? ''}" data-title="${this.escapeHtml(p.title || '')}" data-source="collection" data-handle="${p.handle}" data-variant-id="${p.firstVariantId || ''}" data-variant-price="${p.firstVariantPrice || ''}" data-id="col:${p.handle}" type="button">
              <img src="${p.featuredImage?.url ?? ''}" alt="${this.escapeHtml(p.featuredImage?.altText ?? p.title)}" loading="lazy">
              <span>${this.escapeHtml(p.title)}</span>
            </button>`).join('')}
        </div>
        ${more}`;
    }

    tplUpload() {
      const previewUrl = this.state.uploadPreviewUrl;
      const uploaded = this.state.uploadPublicUrl;
      const loading = this.state.uploadLoading;
      const err = this.state.uploadError;
      const name = this.state.uploadFileName;
      const size = this.state.uploadFileSize;
      const dims = this.state.uploadDims;
      const minW = this.minUploadWidth;
      const hasFile = !!previewUrl;
      const dimOk = !dims || !minW || dims.w >= minW;
      return `<div class="ds-upload">
        <div class="ds-upload__help">
          <h4>Upload your own design</h4>
          <ul class="ds-steps">
            <li class="ds-steps__item"><span class="ds-steps__num">1</span><span class="ds-steps__text">Choose a high-resolution image (JPG, PNG or WebP, up to ${this.uploadMaxMb}&nbsp;MB).</span></li>
            <li class="ds-steps__item"><span class="ds-steps__num">2</span><span class="ds-steps__text">Your image must be at least <strong>${minW}&nbsp;px wide</strong> for a crisp print.</span></li>
            <li class="ds-steps__item"><span class="ds-steps__num">3</span><span class="ds-steps__text">Once uploaded, you'll set the wall dimensions and preview it in a room.</span></li>
          </ul>
        </div>
        <label class="ds-upload__drop ${loading ? 'is-loading' : ''} ${hasFile ? 'has-file' : ''}" data-upload-drop>
          <input type="file" accept="image/jpeg,image/png,image/webp" hidden data-design-upload>
          ${previewUrl
            ? `<img class="ds-upload__preview" src="${previewUrl}" alt="Your upload">`
            : `<div class="ds-upload__placeholder">
                <span class="ds-upload__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4m0 0L7 9m5-5 5 5"/><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/></svg>
                </span>
                <span class="ds-upload__hint">Drag &amp; drop your image here<br><span class="ds-upload__hint-link">or click to browse</span></span>
                <span class="ds-upload__meta">JPG, PNG or WebP · up to ${this.uploadMaxMb}&nbsp;MB · min ${minW}px wide</span>
              </div>`}
          ${loading ? `<div class="ds-upload__overlay"><div class="ds-spinner"></div><span>Uploading…</span></div>` : ''}
        </label>
        ${hasFile && (name || size || dims) ? `
          <div class="ds-upload__fileinfo">
            <span class="ds-upload__file-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5a2 2 0 0 1 2-2h8l6 6v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><path d="M14 3v6h6"/></svg>
            </span>
            <div class="ds-upload__file">
              <span class="ds-upload__file-name">${this.escapeHtml(name || 'Selected image')}</span>
              <span class="ds-upload__file-sub">${size ? this.formatBytes(size) : ''}${dims ? `${size ? ' · ' : ''}${dims.w}×${dims.h}px` : ''}</span>
            </div>
            ${dims ? `<span class="ds-upload__badge ${dimOk ? 'is-ok' : 'is-warn'}">${dimOk ? 'Print ready' : `Needs ≥ ${minW}px`}</span>` : ''}
            <button type="button" class="ds-btn button button--outline button--sm" data-action="upload-clear">Replace</button>
          </div>
        ` : ''}
        ${err ? `<p class="ds-error">${this.escapeHtml(err)}</p>` : ''}
        ${uploaded ? `<p class="ds-upload__success">Image uploaded — continue to dimensions.</p>` : ''}
      </div>`;
    }

    tplAi() {
      const examples = [
        'soft botanical leaves, sage green and gold',
        'art deco geometric, navy and brass',
        'watercolor cherry blossoms on cream',
        'minimalist arches, terracotta and sand',
      ];
      const n = this.aiDesignsPerGen;
      const designWord = n > 1 ? `${n} designs` : 'a design';
      const outputPrompt = (this.state.aiOutputPrompt || this.state.aiPrompt || '').trim();
      const currentOutputs = new Set(this.state.aiOutputs || []);
      const savedDesigns = (this.state.aiHistory || []).filter((design) => !currentOutputs.has(design.imageUrl));
      return `<div class="ds-ai">
        <div class="ds-ai-help">
          <h4>How to generate</h4>
          <ol>
            <li>Describe the style, colours and motif you want (the more specific, the better).</li>
            <li>Click <strong>Generate ${designWord}</strong> — takes about 10–20 seconds.</li>
            <li>Tap a design to select it, then continue to the room preview.</li>
          </ol>
        </div>
        <label class="ds-field">
          <span class="ds-field__label">Describe your wallpaper</span>
          <textarea class="ds-field__input ds-field__input--textarea" rows="3" placeholder="e.g. soft botanical with sage greens and gold accents" data-bind="aiPrompt">${this.state.aiPrompt || ''}</textarea>
        </label>
        <div class="ds-ai-examples">
          <span class="ds-ai-examples__label">Try:</span>
          ${examples.map((ex) => `<button type="button" class="ds-chip" data-action="ai-example" data-prompt="${ex}">${ex}</button>`).join('')}
        </div>
        ${!this.customerId ? `
        <div class="ds-auth-notice" role="note">
          <span class="ds-auth-notice__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
          </span>
          <span class="ds-auth-notice__copy"><strong>Log in to generate AI wallpaper</strong><span>Your designs will be saved to your account so you can return to them later.</span></span>
          <a class="ds-btn button ds-auth-notice__button" href="${this.escapeHtml(this.loginUrl)}">Log in</a>
        </div>` : `<div>
          ${(() => {
            const remaining = Math.max(0, this.aiGenerationLimit - this.state.aiGenerationsUsed);
            const noPrompt = !(this.state.aiPrompt || '').trim();
            const disabled = this.state.aiLoading || noPrompt || remaining <= 0;
            const label = this.state.aiLoading
              ? 'Generating…'
              : remaining <= 0
                ? 'No generations left'
                : `Generate ${designWord} (${remaining} of ${this.aiGenerationLimit} left)`;
            return `<button class="ds-btn button" data-action="ai-generate" ${disabled ? 'disabled' : ''}>${label}</button>`;
          })()}
        </div>`}
        ${this.state.aiError ? `<p class="ds-error">${this.state.aiError}</p>` : ''}
        <div class="ds-cards">
          ${this.state.aiLoading
            ? Array.from({ length: n }).map((_, i) => `
              <div class="ds-card ds-card--skel" aria-busy="true">
                <div class="ds-skel-img"><div class="ds-spinner"></div></div>
                <span>Design ${i + 1}</span>
              </div>`).join('')
            : (this.state.aiOutputs || []).map((url, i) => `
              <button class="ds-card ${this.isChosen('ai:'+i) ? 'is-chosen' : ''}" data-action="open-image" data-image="${url}" data-id="ai:${i}" data-source="ai" data-title="${this.escapeHtml(outputPrompt || `AI design ${i + 1}`)}" data-prompt="${this.escapeHtml(outputPrompt)}" type="button">
                <img src="${url}" alt="${this.escapeHtml(outputPrompt || `AI design ${i + 1}`)}" loading="lazy">
                <span>${this.escapeHtml(outputPrompt || `Design ${i + 1}`)}</span>
              </button>`).join('')}
        </div>
        ${this.customerId && savedDesigns.length ? `
        <div class="ds-ai-history">
          <div class="ds-ai-history__head"><h4 class="ds-ai-history__title">Your saved designs</h4></div>
          <div class="ds-cards">
            ${savedDesigns.map((design) => {
              const title = (design.prompt || 'Saved design').trim();
              return `<button class="ds-card ${this.isChosen(design.id) ? 'is-chosen' : ''}" data-action="open-image" data-image="${design.imageUrl}" data-id="${design.id}" data-source="ai" data-title="${this.escapeHtml(title)}" data-prompt="${this.escapeHtml(design.prompt || '')}" type="button">
                <img src="${design.imageUrl}" alt="${this.escapeHtml(title)}" loading="lazy">
                <span>${this.escapeHtml(title)}</span>
              </button>`;
            }).join('')}
          </div>
        </div>` : ''}
      </div>`;
    }

    /* ---------------- Step 3: Crop ---------------- */
    /* ---------------- Step 3: Room (choose → preview) ---------------- */
    tplRoom() {
      const wallpaper = this.state.chosen[0]?.imageUrl;
      if (!wallpaper) {
        return `
          ${this.stepHeading(3, 'Preview in a room')}
          <p class="ds-error">Pick a design first (step 2) to preview it on a wall.</p>
          <div class="ds-actions">
            <button class="ds-btn button button--outline" data-action="back">Back</button>
            <button class="ds-btn button" data-action="next" disabled>Next: review</button>
          </div>`;
      }
      const presets = this.roomPresets || [];
      const roomUrl = this.state.roomUrl;
      const isUpload = this.state.roomSource === 'upload';

      if (!roomUrl) {
        const remaining = Math.max(0, Number(this.state.roomFitRemaining) || 0);
        const limit = Math.max(1, Number(this.state.roomFitLimit) || 5);
        const usageLabel = this.state.roomFitUsageLoading ? 'Checking preview allowance…' : `${remaining} of ${limit} AI previews available`;
        return `
          ${this.stepHeading(3, 'Choose your room')}
          <p class="ds-preview-hint">Pick a room to see your wallpaper in context — or upload a photo of your own space.</p>
          <div class="ds-room-choose">
            ${presets.map((p) => `
              <button type="button" class="ds-room-choice" data-action="preset-room" data-url="${p.url}" data-quad="${p.quad || ''}">
                <img src="${p.url}" alt="${p.label}" loading="lazy">
                <span>${p.label}</span>
              </button>
            `).join('')}
            ${this.enableRoomUpload ? (this.customerId ? (remaining > 0 ? `
              <label class="ds-room-choice ds-room-choice--upload ${this.state.roomFitUsageLoading ? 'is-disabled' : ''}">
                <input type="file" accept="image/*" data-room-upload ${this.state.roomFitUsageLoading ? 'disabled' : ''} hidden>
                <span class="ds-beta-badge">Beta</span>
                <span class="ds-room-choice__icon" aria-hidden="true">📷</span>
                <span>Upload your room</span>
                <small class="ds-room-choice__limit">${usageLabel}</small>
              </label>` : `
              <div class="ds-room-choice ds-room-choice--upload is-disabled" aria-disabled="true">
                <span class="ds-beta-badge">Beta</span>
                <span class="ds-room-choice__icon" aria-hidden="true">📷</span>
                <span>Preview limit reached</span>
                <small class="ds-room-choice__limit">0 of ${limit} AI previews available · resets within 24 hours</small>
              </div>`) : `
              <a class="ds-room-choice ds-room-choice--upload ds-room-preset--login" href="${this.escapeHtml(this.loginUrl)}">
                <span class="ds-room-choice__icon" aria-hidden="true">↗</span>
                <span>Log in to upload<br>your own room</span>
              </a>`) : ''}
          </div>
          <div class="ds-actions">
            <button class="ds-btn button button--outline" data-action="back">Back</button>
          </div>`;
      }

      const bw = !!this.state.bwMode;
      if (isUpload) {
        const fitUrl = this.state.roomFitUrl;
        const loading = !!this.state.roomFitLoading;
        const error = this.state.roomFitError;
        const split = Math.max(0, Math.min(100, Number(this.state.roomCompare) || 50));
        return `
          ${this.stepHeading(3, 'Preview in a room')}
          <p class="ds-preview-hint">We place your wallpaper on your main wall automatically. Visual preview only — the final cut is decided in the next step.</p>
          <p class="ds-beta-note"><span class="ds-beta-badge">Beta</span> AI room preview is experimental — the placement may not perfectly match your space.</p>
          <div class="ds-room-preview ds-room-preview--full">
            <div class="ds-stage-wrap">
              <div class="ds-room-stage ds-room-stage--ai ${loading ? 'is-loading' : ''}" data-stage>
                ${fitUrl ? `
                <div class="ds-room-comparison" data-room-comparison style="--ds-room-split:${split}%">
                  <img class="ds-room-img ds-room-comparison__after" src="${fitUrl}" alt="Room with wallpaper"${bw ? ' style="filter:grayscale(1)"' : ''}>
                  <div class="ds-room-comparison__before"><img src="${roomUrl}" alt="Original room"></div>
                  <span class="ds-room-comparison__label ds-room-comparison__label--before">Before</span>
                  <span class="ds-room-comparison__label ds-room-comparison__label--after">After</span>
                  <button class="ds-room-comparison__divider" type="button" data-room-compare role="slider" aria-label="Compare original room and wallpaper preview" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(split)}"><span aria-hidden="true">↔</span></button>
                </div>` : `<img class="ds-room-img" src="${roomUrl}" alt="Room preview">`}
                ${error ? `<div class="ds-room-error" role="alert"><span>${this.escapeHtml(error)}</span></div>` : ''}
              </div>
            </div>
          </div>
          <div class="ds-actions">
            <button class="ds-btn button button--outline" data-action="change-room" ${loading ? 'disabled' : ''}>Choose another room</button>
            <button class="ds-btn button" data-action="next" ${fitUrl && !loading ? '' : 'disabled'}>Next: review</button>
          </div>`;
      }

      return `
        ${this.stepHeading(3, 'Preview in a room')}
        <p class="ds-preview-hint">Here’s your wallpaper in the room. Visual preview only — the final cut is decided in the next step.</p>

        <div class="ds-room-layout">
          <div class="ds-room-preview">
            ${roomUrl ? `
              <div class="ds-stage-wrap">
                <div class="ds-room-stage" data-stage>
                  <img class="ds-room-img" data-room-img src="${roomUrl}" alt="Room" crossorigin="anonymous">
                  <div class="ds-wall" data-wall style="background-image:url('${wallpaper}');background-size:cover;background-position:center;background-repeat:no-repeat;${bw ? 'filter:grayscale(1);' : ''}"></div>
                </div>
              </div>
            ` : '<p class="ds-preview-hint">Choose a room to see your wallpaper in context.</p>'}
          </div>

          <aside class="ds-room-picker">
            <h4 class="ds-room-picker__title">Choose a room</h4>
            <div class="ds-room-presets">
              ${presets.map((p) => `
                <button type="button" class="ds-room-preset ${roomUrl === p.url ? 'is-selected' : ''}"
                        data-action="preset-room" data-url="${p.url}" data-quad="${p.quad || ''}">
                  <img src="${p.url}" alt="${p.label}">
                  <span>${p.label}</span>
                </button>
              `).join('')}
            </div>
          </aside>
        </div>

        <div class="ds-actions">
          <button class="ds-btn button button--outline" data-action="back">Back</button>
          <button class="ds-btn button" data-action="next">Next: review</button>
        </div>`;
    }

    /** Default crop centred, aspect = widthCm:heightCm, as percentages of image. */
    defaultCrop() {
      const d = this.state.dims;
      const ratio = (d.width || 1) / (d.height || 1); // wall aspect (w/h)
      // We pick the largest centred rectangle of the given aspect that fits 100×100%.
      // crop.w/crop.h are in % of image natural dimensions; without knowing the image
      // aspect yet, we use 80% width and derive height — clamp() will be applied at first drag.
      const w = 80;
      const h = Math.min(100, w / ratio);
      return { x: (100 - w) / 2, y: (100 - h) / 2, w, h };
    }

    /* ---------------- Step 4: Review (crop + colour + dims + price) ---------------- */
    tplReview() {
      const c = this.calc();
      const d = this.state.dims;
      const choice = this.state.chosen[0];
      const designUrl = choice?.imageUrl;
      const bw = !!this.state.bwMode;
      const flip = !!this.state.flip;
      const ratio = (d.width && d.height) ? `${d.width} / ${d.height}` : '3 / 4';
      const tr = this.state.imgTransform || { tx: 0, ty: 0, scale: 1 };

      const imgStyle = designUrl
        ? `transform:translate(-50%,-50%) translate(${tr.tx}px,${tr.ty}px) scale(${tr.scale}) ${flip ? 'scaleX(-1)' : ''};${bw ? 'filter:grayscale(1);' : ''}`
        : '';

      return `
        ${this.stepHeading(4, 'Finalise your wallpaper')}
        <p class="ds-preview-hint">Drag the image to position it inside the printable area. Use the buttons to zoom and flip. The dashed box is the final cut at <strong>${d.width || '—'}&nbsp;cm × ${d.height || '—'}&nbsp;cm</strong>.</p>

        <div class="ds-review-layout">
          <div class="ds-final-wrap">
            <div class="ds-final-canvas" data-final-canvas>
              ${designUrl
                ? `<img class="ds-final-img" data-final-img src="${designUrl}" alt="Design" style="${imgStyle}" draggable="false">`
                : ''}
              <div class="ds-final-frame" data-final-frame style="aspect-ratio:${ratio};" aria-label="Final cut area">
                <span class="ds-final-label">${d.width || '—'}&nbsp;cm × ${d.height || '—'}&nbsp;cm</span>
              </div>
              ${designUrl ? `
                <button type="button" class="ds-flip-fab ${flip ? 'is-on' : ''}" data-action="toggle-flip" aria-pressed="${flip ? 'true' : 'false'}" aria-label="Flip horizontal" title="${flip ? 'Flipped (tap to undo)' : 'Flip horizontal'}">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M16 7l4 5-4 5"/><path d="M8 7l-4 5 4 5"/></svg>
                </button>
              ` : ''}
              ${!designUrl ? `<p class="ds-error ds-error--centred">No design selected.</p>` : ''}
            </div>
          </div>

          <aside class="ds-review-panel">
            <div class="ds-final-bar ds-final-bar--stack" role="toolbar" aria-label="Edit wallpaper">
              <div class="ds-final-bar__row">
                <button type="button" class="ds-tool ${flip ? 'is-on' : ''}" data-action="toggle-flip" aria-label="Flip horizontal" aria-pressed="${flip ? 'true' : 'false'}">
                  <span class="ds-tool__icon">⇄</span><span class="ds-tool__label">${flip ? 'Flipped' : 'Flip'}</span>
                </button>
                <button type="button" class="ds-tool" data-action="zoom-in" aria-label="Zoom in">
                  <span class="ds-tool__icon">＋</span><span class="ds-tool__label">Zoom in</span>
                </button>
                <button type="button" class="ds-tool" data-action="zoom-out" aria-label="Zoom out">
                  <span class="ds-tool__icon">−</span><span class="ds-tool__label">Zoom out</span>
                </button>
                <button type="button" class="ds-tool" data-action="reset-crop" aria-label="Reset">
                  <span class="ds-tool__icon">✕</span><span class="ds-tool__label">Reset</span>
                </button>
              </div>
              <div class="ds-segmented" role="tablist" aria-label="Colour mode">
                <button class="ds-seg ${!bw ? 'is-on' : ''}" data-action="bw-toggle" data-bw="0">Colour</button>
                <button class="ds-seg ${bw ? 'is-on' : ''}" data-action="bw-toggle" data-bw="1">B&amp;W</button>
              </div>
              <div class="ds-final-bar__row">
                <label class="ds-field ds-field--compact ds-field--inline">
                  <span class="ds-field__label">W (cm)</span>
                  <input class="ds-field__input" type="number" min="10" step="1" data-bind="dims.width" value="${d.width || ''}">
                </label>
                <label class="ds-field ds-field--compact ds-field--inline">
                  <span class="ds-field__label">H (cm)</span>
                  <input class="ds-field__input" type="number" min="10" step="1" data-bind="dims.height" value="${d.height || ''}">
                </label>
              </div>
              ${this.renderMinOrderNotice()}
            </div>

            <div class="ds-review-summary">
              ${this.renderSummary()}
            </div>

            ${this.renderPosterInstructions()}
          </aside>
        </div>

        ${this.renderCartError()}
        <div class="ds-actions">
          <button class="ds-btn button button--outline" data-action="back">Back</button>
          <button class="ds-btn button" data-action="add-to-cart" ${this.state.cartLoading || c.belowMinimum ? 'disabled' : ''}>
            ${this.state.cartLoading ? 'Adding…' : (c.belowMinimum ? `Minimum order ${this.minOrderM2.toFixed(2)} m²` : `Add to cart · ${this.money(c.total)}`)}
          </button>
        </div>`;
    }

    /* ---------------- Summary sidebar ---------------- */
    posterEligible() {
      const w = Number(this.state.dims && this.state.dims.width) || 0;
      const h = Number(this.state.dims && this.state.dims.height) || 0;
      // Poster option only makes sense at smaller sizes; hide once either
      // dimension reaches the roll-print threshold.
      return (w > 0 && w < 145) || (h > 0 && h < 145);
    }

    renderPosterInstructions() {
      if (!this.posterEligible()) {
        if (this.state.posterInstructions) this.state.posterInstructions = false;
        return '<div data-poster-slot></div>';
      }
      const on = !!this.state.posterInstructions;
      const label = this.posterInstructionsLabel || 'Do you want this as a poster (no excess, printed to exact size)?';
      const tip = this.posterInstructionsTooltip || '';
      return `
        <div data-poster-slot>
          <div class="ds-poster ${on ? 'is-on' : ''}">
            <label class="ds-poster__row">
              <input type="checkbox" class="ds-poster__check" data-action="toggle-poster" ${on ? 'checked' : ''}>
              <span class="ds-poster__label">${label}</span>
              ${tip ? `
                <span class="ds-tooltip" tabindex="0" aria-label="More info">
                  <span class="ds-tooltip__icon" aria-hidden="true">i</span>
                  <span class="ds-tooltip__bubble" role="tooltip">${tip}</span>
                </span>
              ` : ''}
            </label>
          </div>
        </div>`;
    }

    renderSummary() {
      const c = this.calc();
      const d = this.state.dims;
      return `
        <h3>Order summary</h3>
        <dl>
          <dt>Wall size</dt><dd>${d.width || '—'} × ${d.height || '—'} cm</dd>
          <dt>Wall area</dt><dd>${c.wallAreaM2.toFixed(2)} m²</dd>
          ${this.pricePerM2 > 0 ? `<dt>Price / m²</dt><dd>${this.money(this.pricePerM2)}</dd>` : ''}
          ${this.state.wastageEnabled ? `<dt>Wastage</dt><dd>${this.state.wastageOn ? `${d.wastage || 0}%` : 'Off'}</dd>` : ''}
          <dt>Designs</dt><dd>${this.state.chosen.length}</dd>
        </dl>
        ${c.belowMinimum ? `<p class="ds-summary__min-note" role="alert">Below the ${this.minOrderM2.toFixed(2)}&nbsp;m² minimum order — increase the wall size to continue.</p>` : ''}
        <div class="ds-summary__total"><span>Total</span><span>${c.belowMinimum ? '—' : this.money(c.total)}</span></div>
        ${this.leadTime ? `<p class="ds-summary__lead">${this.leadTime}</p>` : ''}`;
    }

    renderModal() {
      const m = this.state.modal;
      if (!m || !m.imageUrl) return '';
      return `
        <div class="ds-modal" data-action="close-modal" role="dialog" aria-modal="true">
          <div class="ds-modal__panel" data-stop>
            <button class="ds-modal__close" data-action="close-modal" aria-label="Close" type="button">×</button>
            <div class="ds-modal__media">
              <img src="${m.imageUrl}" alt="${m.title || 'Preview'}">
            </div>
            <div class="ds-modal__body">
              <h3 class="ds-modal__title h3">${m.title || 'Preview'}</h3>
              ${m.prompt && m.prompt !== m.title ? `<p class="ds-modal__prompt">"${m.prompt}"</p>` : ''}
              <div class="ds-modal__actions">
                <button class="ds-btn button button--outline" data-action="close-modal" type="button">Close</button>
                <button class="ds-btn button" data-action="pick-design" type="button">Use this design</button>
              </div>
            </div>
          </div>
        </div>`;
    }

    /** Full-screen processing overlay shown while adding to cart. Displays the
     * current stage (cropping → creating size → adding → redirecting) so the
     * customer knows work is happening behind the scenes. */
    renderCartOverlay() {
      if (!this.state.cartLoading) return '';
      const stages = [
        { key: 'crop', label: 'Preparing your print' },
        { key: 'create', label: 'Building your custom size' },
        { key: 'add', label: 'Adding to your cart' },
        { key: 'done', label: 'Taking you to your cart' },
      ];
      const current = this.state.cartStage || 'crop';
      let currentIdx = stages.findIndex((s) => s.key === current);
      if (currentIdx < 0) currentIdx = 0;
      const steps = stages.map((s, i) => {
        const state = i < currentIdx ? 'is-done' : (i === currentIdx ? 'is-active' : '');
        const mark = i < currentIdx ? '✓' : '';
        return `<li class="ds-cart-overlay__step ${state}"><span class="ds-cart-overlay__dot">${mark}</span>${s.label}</li>`;
      }).join('');
      return `
        <div class="ds-cart-overlay" role="status" aria-live="polite">
          <div class="ds-cart-overlay__panel">
            <div class="ds-spinner ds-cart-overlay__spinner"></div>
            <p class="ds-cart-overlay__title">${stages[currentIdx].label}…</p>
            <ul class="ds-cart-overlay__steps">${steps}</ul>
          </div>
        </div>`;
    }

    renderRoomFitOverlay() {
      if (!this.state.roomFitLoading) return '';
      const stages = [
        { key: 'upload', label: 'Uploading your room' },
        { key: 'analyse', label: 'Analysing your main wall' },
        { key: 'place', label: 'Placing your wallpaper' },
      ];
      const current = this.state.roomFitStage || 'upload';
      let currentIdx = stages.findIndex((stage) => stage.key === current);
      if (currentIdx < 0) currentIdx = 0;
      const steps = stages.map((stage, index) => {
        const state = index < currentIdx ? 'is-done' : index === currentIdx ? 'is-active' : '';
        return `<li class="ds-cart-overlay__step ${state}"><span class="ds-cart-overlay__dot">${index < currentIdx ? '✓' : ''}</span>${stage.label}</li>`;
      }).join('');
      return `
        <div class="ds-cart-overlay" role="status" aria-live="polite">
          <div class="ds-cart-overlay__panel">
            <div class="ds-spinner ds-cart-overlay__spinner"></div>
            <p class="ds-cart-overlay__title">${stages[currentIdx].label}…</p>
            <ul class="ds-cart-overlay__steps">${steps}</ul>
            <p class="ds-cart-overlay__note">This usually takes 10–30 seconds. Please keep this tab open.</p>
          </div>
        </div>`;
    }

    /** Update the add-to-cart processing stage (re-renders the overlay). */
    setCartStage(stage) { this.setState({ cartStage: stage }); }

    isChosen(id) { return this.state.chosen.some((c) => c.id === id); }

    /* ---------------- Events ---------------- */
    onAction(e) {
      const el = e.currentTarget;
      const a = el.dataset.action;
      if (this.state.roomFitLoading && ['back', 'goto-step', 'change-room', 'next', 'preset-room'].includes(a)) return;
      if (a === 'next') {
        const nextStep = Math.min(4, this.state.step + 1);
        this.setState({ step: nextStep });
      }
      else if (a === 'back') this.setState({ step: Math.max(1, this.state.step - 1) });
      else if (a === 'goto-step') {
        const n = Number(el.dataset.step) || 1;
        if (n <= this.state.step) this.setState({ step: n });
      }
      else if (a === 'tab') {
        const t = el.dataset.tab;
        const patch = { sourceTab: t ? t : null };
        // Switching away from a tab clears any source-specific state so the user
        // gets a fresh view when they come back.
        if (t !== 'browse') patch.collection = null;
        this.setState(patch);
      }
      else if (a === 'dims-mode') this.setState({ knowsDims: el.dataset.known === '1' });
      else if (a === 'toggle-wastage') this.setState({ wastageOn: !!el.checked });
      else if (a === 'room-mode') this.setState({ roomSource: el.dataset.mode, previewUrl: null });
      else if (a === 'open-image') {
        const d = el.dataset;
        const prompt = d.source === 'ai' ? (d.prompt || this.state.aiOutputPrompt || this.state.aiPrompt || '').trim() : '';
        this.setState({ modal: {
          id: d.id || ('img:' + d.image),
          imageUrl: d.image,
          title: d.title || prompt || '',
          source: d.source || 'ai',
          handle: d.handle || null,
          variantId: d.variantId || null,
          variantPrice: d.variantPrice ? Number(d.variantPrice) : null,
          prompt,
        }});
      }
      else if (a === 'close-modal') {
        this.setState({ modal: null });
      }
      else if (a === 'goto-checkout') {
        window.location.href = this.cartUrl || '/cart';
      }
      else if (a === 'pick-design') {
        const m = this.state.modal;
        if (m && m.imageUrl) {
          const item = m.source === 'collection'
            ? {
                id: 'col:' + m.handle,
                imageUrl: m.imageUrl,
                source: 'collection',
                title: m.title,
                handle: m.handle,
                variantId: m.variantId || null,
                variantPrice: m.variantPrice || null,
              }
            : { id: m.id, imageUrl: m.imageUrl, source: 'ai', prompt: m.prompt, title: m.title };
          this.setState({ chosen: [item], modal: null, step: Math.max(this.state.step, 3) });
        }
      }
      else if (a === 'ai-generate') {
        if (!this.customerId) window.location.href = this.loginUrl;
        else this.generateAi();
      }
      else if (a === 'ai-example') { this.setState({ aiPrompt: el.dataset.prompt }); }
      else if (a === 'preset-room') this.selectPresetRoom(el.dataset.url, el.dataset.quad);
      else if (a === 'browse-more') this.fetchCollection({ reset: false });
      else if (a === 'browse-search') {
        this.state.collection = null;
        this.fetchCollection({ reset: true });
        this.render();
      }
      else if (a === 'upload-clear') {
        this.setState({
          uploadPreviewUrl: null, uploadPublicUrl: null, uploadError: null,
          uploadFileName: null, uploadFileSize: null, uploadDims: null, uploadLoading: false,
          chosen: this.state.chosen.filter((c) => c.source !== 'upload'),
        });
      }
      else if (a === 'change-wallpaper') this.setState({ step: 2 });
      else if (a === 'change-room') {
        this.setState({ roomUrl: null, roomPublicUrl: null, roomQuad: null, roomSource: null, previewUrl: null, previewError: null, roomFitUrl: null, roomFitWallpaperUrl: null, roomFitLoading: false, roomFitError: null });
      }
      else if (a === 'bw-toggle') {
        this.state.bwMode = el.dataset.bw === '1';
        // Update segmented buttons without full re-render to preserve drag state.
        this.querySelectorAll('[data-action="bw-toggle"]').forEach((b) => {
          b.classList.toggle('is-on', b.dataset.bw === el.dataset.bw);
        });
        this.applyFinalTransform();
      }
      else if (a === 'toggle-flip') {
        this.state.flip = !this.state.flip;
        const on = this.state.flip;
        // Update ALL flip controls (toolbar tool + canvas FAB) in place so the
        // icon is never clobbered and the pan/zoom state is preserved.
        this.querySelectorAll('[data-action="toggle-flip"]').forEach((btn) => {
          btn.classList.toggle('is-on', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          const lbl = btn.querySelector('.ds-tool__label');
          if (lbl) lbl.textContent = on ? 'Flipped' : 'Flip';
          if (btn.classList.contains('ds-flip-fab')) btn.title = on ? 'Flipped (tap to undo)' : 'Flip horizontal';
        });
        this.applyFinalTransform();
      }
      else if (a === 'reset-crop') {
        this.fitFinalImage();
      }
      else if (a === 'zoom-in') this.stepZoom(1.2);
      else if (a === 'zoom-out') this.stepZoom(1 / 1.2);
      else if (a === 'reset-quad') this.setState({ roomQuad: [
        { x: 0, y: 0 }, { x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 },
      ] });
      else if (a === 'add-to-cart') this.addToCart();
      else if (a === 'toggle-poster') {
        this.state.posterInstructions = !!el.checked;
        const wrap = this.querySelector('.ds-poster');
        if (wrap) wrap.classList.toggle('is-on', this.state.posterInstructions);
      }
    }

    onBind(e) {
      const path = e.currentTarget.dataset.bind;
      const value = e.currentTarget.type === 'number' ? Number(e.currentTarget.value) : e.currentTarget.value;
      if (path.startsWith('dims.')) {
        this.state.dims[path.slice(5)] = value;
        // If the user has now provided both width and height, treat as known
        // even if Step 1 had "I'll measure later" selected.
        if (Number(this.state.dims.width) >= 10 && Number(this.state.dims.height) >= 10) {
          this.state.knowsDims = true;
        }
        const s = this.querySelector('.ds-summary, .ds-review-summary');
        if (s) s.innerHTML = this.renderSummary();
        // Live-update Review step: frame aspect, label and total.
        const frame = this.querySelector('[data-final-frame]');
        if (frame && this.state.dims.width && this.state.dims.height) {
          frame.style.aspectRatio = `${this.state.dims.width} / ${this.state.dims.height}`;
        }
        const label = this.querySelector('.ds-final-label');
        if (label) label.innerHTML = `${this.state.dims.width || '—'}&nbsp;cm × ${this.state.dims.height || '—'}&nbsp;cm`;
        const cta = this.querySelector('[data-action="add-to-cart"]');
        if (cta && !this.state.cartLoading) {
          const c2 = this.calc();
          // Below the minimum the wall cannot be ordered, so the CTA is
          // disabled and relabelled rather than showing a price.
          cta.toggleAttribute('disabled', c2.belowMinimum);
          cta.textContent = c2.belowMinimum
            ? `Minimum order ${this.minOrderM2.toFixed(2)} m²`
            : `Add to cart · ${this.money(c2.total)}`;
        }
        // Keep the minimum-order notice and the Step 2 "Next" button in sync.
        this.querySelectorAll('[data-min-order-slot]').forEach((slot) => {
          slot.innerHTML = this.minOrderNoticeInner();
        });
        const nextBtn = this.querySelector('[data-action="next"]');
        if (nextBtn) nextBtn.toggleAttribute('disabled', !this.canConfirmDims());
        // Toggle the poster/hanging-instructions block based on updated dims.
        const posterSlot = this.querySelector('[data-poster-slot]');
        if (posterSlot) posterSlot.outerHTML = this.renderPosterInstructions();
        // Frame aspect changed — clamp current pan in case image moved out of bounds.
        requestAnimationFrame(() => { this.clampFinalTransform(); this.applyFinalTransform(); });
      } else {
        this.state[path] = value;
        // Toggle the generate button live without re-rendering (preserves focus/caret).
        if (path === 'aiPrompt') {
          const btn = this.querySelector('[data-action="ai-generate"]');
          const remaining = Math.max(0, this.aiGenerationLimit - this.state.aiGenerationsUsed);
          if (btn) btn.disabled = this.state.aiLoading || !String(value || '').trim() || remaining <= 0;
        }
      }
    }

    toggleChoice(item) {
      const exists = this.state.chosen.find((c) => c.id === item.id);
      const chosen = exists ? this.state.chosen.filter((c) => c.id !== item.id) : [...this.state.chosen, item];
      this.setState({ chosen });
    }

    /* ---------------- Backend calls ---------------- */
    async fetchCollection({ reset = false } = {}) {
      if (this.state.collectionLoading) return;
      const cursor = reset ? null : (this.state.collection?.pageInfo?.endCursor ?? null);
      const search = (this.state.collectionSearch || '').trim();
      this.state.collectionLoading = true;
      try {
        const url = new URL(`${this.proxy}/collection`, window.location.origin);
        if (cursor) url.searchParams.set('cursor', cursor);
        if (search) url.searchParams.set('search', search);
        if (this.excludedTags && this.excludedTags.length) url.searchParams.set('excludeTags', this.excludedTags.join(','));
        const r = await fetch(url.toString(), { credentials: 'same-origin' });
        const data = await r.json();
        const rawProducts = Array.isArray(data.products) ? data.products : [];
        // Never surface the hidden shared custom-wallpaper product, or any product without an image.
        const products = rawProducts.filter((p) => {
          const h = String(p.handle || '').toLowerCase();
          const t = String(p.title || '').toLowerCase();
          if (h === 'pickawall-custom-wallpaper' || t.startsWith('pickawall custom wallpaper')) return false;
          return !!(p.featuredImage && p.featuredImage.url);
        });
        const pageInfo = data.pageInfo || { hasNextPage: false, endCursor: null };
        const prev = reset ? [] : (this.state.collection?.products || []);
        this.state.collectionLoading = false;
        this.setState({ collection: { products: [...prev, ...products], pageInfo } });
      } catch {
        this.state.collectionLoading = false;
        this.setState({ collection: { products: [], pageInfo: { hasNextPage: false, endCursor: null } } });
      }
    }

    /** Step 1 · Upload your own design — presigned R2 upload via /upload-design. */
    async onDesignUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      const maxBytes = this.uploadMaxMb * 1024 * 1024;
      if (file.size > maxBytes) {
        this.setState({ uploadError: `Image is too large (max ${this.uploadMaxMb} MB).`, uploadPreviewUrl: null, uploadPublicUrl: null, uploadFileName: null, uploadFileSize: null, uploadDims: null });
        return;
      }
      const allowed = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };
      const ext = allowed[file.type];
      if (!ext) {
        this.setState({ uploadError: 'Unsupported file type. Please use JPG, PNG or WebP.', uploadPreviewUrl: null, uploadPublicUrl: null, uploadFileName: null, uploadFileSize: null, uploadDims: null });
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      // Measure the image so we can enforce the minimum print width and show details.
      let dims = null;
      try { dims = await this.measureImage(previewUrl); } catch { dims = null; }
      if (dims && this.minUploadWidth && dims.w < this.minUploadWidth) {
        this.setState({
          uploadError: `This image is only ${dims.w}px wide. Please upload an image at least ${this.minUploadWidth}px wide for a sharp print.`,
          uploadPreviewUrl: previewUrl,
          uploadPublicUrl: null,
          uploadFileName: file.name,
          uploadFileSize: file.size,
          uploadDims: dims,
          uploadLoading: false,
        });
        return;
      }
      this.setState({ uploadLoading: true, uploadError: null, uploadPreviewUrl: previewUrl, uploadPublicUrl: null, uploadFileName: file.name, uploadFileSize: file.size, uploadDims: dims });
      try {
        const sign = await fetch(`${this.proxy}/upload-design`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ contentType: file.type, ext }),
        });
        if (!sign.ok) throw new Error(`Upload request failed (${sign.status}).`);
        const { uploadUrl, publicUrl } = await sign.json();
        const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        if (!put.ok) throw new Error(`Upload failed (${put.status}).`);
        const baseName = file.name.replace(/\.[^.]+$/, '').trim();
        const shortName = baseName.length > 24 ? `${baseName.slice(0, 24)}…` : baseName;
        const item = {
          id: `up:${publicUrl}`,
          imageUrl: publicUrl,
          source: 'upload',
          title: shortName ? `Upload your own - ${shortName}` : 'Upload your own',
        };
        this.setState({ uploadLoading: false, uploadPublicUrl: publicUrl, chosen: [item] });
      } catch (err) {
        this.setState({ uploadLoading: false, uploadError: err.message || 'Upload failed.' });
      }
    }

    async generateAi() {
      if (!this.customerId) {
        window.location.href = this.loginUrl;
        return;
      }
      if (document.documentElement.getAttribute('data-pw-geo-blocked') === 'true') {
        this.setState({ aiError: 'Image generation is available inside Australia only. Please send us an international enquiry — we\'ll help arrange your custom design.' });
        const dlg = document.getElementById('pw-geo-dialog');
        if (dlg) { try { if (dlg.showModal && !dlg.open) dlg.showModal(); else dlg.setAttribute('open', ''); } catch (_) { dlg.setAttribute('open', ''); } }
        return;
      }
      const prompt = (this.state.aiPrompt || '').trim();
      if (prompt.length < 3) { this.setState({ aiError: 'Enter at least 3 characters.' }); return; }
      if (this.state.aiGenerationsUsed >= this.aiGenerationLimit) {
        this.setState({ aiError: `You've used all ${this.aiGenerationLimit} generations. The counter resets in 24 hours — or browse our ready-made designs in the meantime.` });
        return;
      }
      const nextUsed = this.state.aiGenerationsUsed + 1;
      this.writePersistedAiUsage(nextUsed);
      this.setState({
        aiLoading: true,
        aiError: null,
        aiOutputs: [],
        aiOutputPrompt: prompt,
        aiGenerationsUsed: nextUsed,
      });
      try {
        const body = { prompt, aspect: '3:4', count: this.aiDesignsPerGen };
        if (this.aiModel) body.model = this.aiModel;
        if (this.aiInstructions && (this.aiInstructions.systemPrompt || this.aiInstructions.styleSuffix || this.aiInstructions.negativePrompt)) {
          body.instructions = this.aiInstructions;
        }
        const r = await fetch(`${this.proxy}/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });
        if (r.status === 429) throw new Error('You\'ve hit the generation limit. Try again in an hour.');
        if (!r.ok) throw new Error('Generation failed.');
        const { jobId } = await r.json();
        this.setState({ aiJobId: jobId });
        await this.pollJob(jobId);
      } catch (err) {
        this.setState({ aiError: err.message || 'Generation failed.', aiLoading: false });
      }
    }

    async pollJob(jobId) {
      const deadline = Date.now() + 150_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        const r = await fetch(`${this.proxy}/jobs/${jobId}`, { credentials: 'same-origin' });
        const data = await r.json();
        if (data.status === 'succeeded') {
          const outputs = data.outputs || [];
          this.rememberAiDesigns(outputs, this.state.aiOutputPrompt);
          this.setState({ aiOutputs: outputs, aiLoading: false });
          return;
        }
        if (data.status === 'failed')    { this.setState({ aiError: data.error || 'Generation failed.', aiLoading: false }); return; }
      }
      // The design can still be finishing on the server past our polling window
      // (busy periods / first generation of the session run slower). Reassure the
      // customer rather than implying a hard failure.
      this.setState({ aiError: 'Your design is taking a little longer than usual — it may still be finishing. Please wait a few seconds and tap Generate again.', aiLoading: false });
    }

    async onRoomUpload(e) {
      if (!this.customerId) {
        e.target.value = '';
        window.location.href = this.loginUrl;
        return;
      }
      const file = e.target.files?.[0];
      if (!file) return;
      if (this.state.roomFitRemaining <= 0) {
        e.target.value = '';
        this.setState({ roomFitError: 'You’ve used all 5 AI room previews. Your allowance resets within 24 hours.' });
        return;
      }
      const wallpaper = this.state.chosen[0]?.imageUrl;
      if (!wallpaper) {
        this.setState({ roomFitError: 'Pick a design first (step 2) to preview it on a wall.' });
        return;
      }
      if (file.size > this.uploadMaxMb * 1024 * 1024) {
        this.setState({ roomFitError: `Room image is too large (max ${this.uploadMaxMb} MB).` });
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(file.type)) {
        this.setState({ roomFitError: 'Unsupported room image. Please use JPG, PNG, WebP or HEIC.' });
        return;
      }
      const url = URL.createObjectURL(file);
      let roomAspectRatio = '1:1';
      try {
        const dimensions = await this.measureImage(url);
        roomAspectRatio = this.closestRoomAspect(dimensions.w, dimensions.h);
      } catch {}
      this.setState({
        roomUrl: url,
        roomPublicUrl: null,
        roomSource: 'upload',
        roomFitUrl: null,
        roomFitWallpaperUrl: null,
        roomFitLoading: true,
        roomFitStage: 'upload',
        roomFitError: null,
        roomFitAspectRatio: roomAspectRatio,
        roomCompare: 50,
        roomQuad: null,
        previewError: null,
        bwMode: false,
      });
      this.fitUploadedRoom(file);
    }

    absoluteUrl(value) {
      if (!value) return value;
      const url = String(value).trim();
      if (url.startsWith('//')) return 'https:' + url;
      if (url.startsWith('/')) return window.location.origin + url;
      return url;
    }

    async fitUploadedRoom(file) {
      try {
        const typeToExt = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic' };
        const contentType = typeToExt[file.type] ? file.type : 'image/jpeg';
        const ext = typeToExt[file.type] || 'jpg';
        const signResponse = await fetch(`${this.proxy}/upload-room`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ contentType, ext }),
        });
        if (signResponse.status === 401) throw new Error('Your session has expired. Log in again to create an AI room preview.');
        if (!signResponse.ok) throw new Error('We couldn’t upload your photo. Please try again.');
        const { uploadUrl, publicUrl } = await signResponse.json();
        const uploadResponse = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
        if (!uploadResponse.ok) throw new Error('We couldn’t upload your photo. Please try again.');
        this.state.roomPublicUrl = publicUrl;
        await this.fitUploadedRoomUrl(publicUrl);
      } catch (error) {
        this.setState({ roomFitError: error.message || 'We couldn’t create your room preview.', roomFitLoading: false, roomFitStage: null });
      }
    }

    async fitUploadedRoomUrl(publicUrl) {
      const wallpaper = this.absoluteUrl(this.state.chosen[0]?.imageUrl);
      if (!publicUrl || !wallpaper || this.state.roomFitLoading && this.state.roomFitStage !== 'upload') return;
      if (this.state.roomFitRemaining <= 0) {
        this.setState({ roomFitError: 'You’ve used all 5 AI room previews. Your allowance resets within 24 hours.', roomFitLoading: false, roomFitStage: null });
        return;
      }
      this.setState({ roomFitUrl: null, roomFitLoading: true, roomFitStage: 'analyse', roomFitError: null });
      try {
        const fitResponse = await fetch(`${this.proxy}/room-fit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ roomUrl: publicUrl, wallpaperUrl: wallpaper, provider: 'hybrid', aspectRatio: this.state.roomFitAspectRatio }),
        });
        if (fitResponse.status === 401) throw new Error('Your session has expired. Log in again to create an AI room preview.');
        const fitData = await fitResponse.json().catch(() => ({}));
        if (fitData.usage || fitData.limit) {
          const usage = fitData.usage || fitData;
          this.state.roomFitLimit = Number(usage.limit) || 5;
          this.state.roomFitRemaining = Math.max(0, Number(usage.remaining) || 0);
        }
        if (fitResponse.status === 429) throw new Error('You’ve used all 5 AI room previews. Your allowance resets within 24 hours.');
        if (!fitResponse.ok) throw new Error('We couldn’t start your room preview. Please try again.');
        const { jobId } = fitData;
        this.setState({ roomFitStage: 'place' });
        const outputs = await this.pollJobResult(jobId);
        if (!outputs.length) throw new Error('We couldn’t fit the wallpaper to your room. Please try another photo.');
        this.setState({ roomFitUrl: outputs[0], roomFitWallpaperUrl: wallpaper, roomFitLoading: false, roomFitStage: null, roomFitError: null });
      } catch (error) {
        this.setState({ roomFitError: error.message || 'We couldn’t create your room preview.', roomFitLoading: false, roomFitStage: null });
      }
    }

    async loadRoomPreviewUsage() {
      this.state.roomFitUsageLoading = true;
      if (this.state.step === 3) this.render();
      try {
        const response = await fetch(`${this.proxy}/room-fit/usage`, { credentials: 'same-origin' });
        if (!response.ok) return;
        const usage = await response.json();
        this.state.roomFitLimit = Number(usage.limit) || 5;
        this.state.roomFitRemaining = Math.max(0, Number(usage.remaining) || 0);
      } catch {} finally {
        this.state.roomFitUsageLoading = false;
        if (this.state.step === 3) this.render();
      }
    }

    async pollJobResult(jobId, timeoutMs = 150000) {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const response = await fetch(`${this.proxy}/jobs/${jobId}`, { credentials: 'same-origin' });
        const data = await response.json();
        if (data.status === 'succeeded') return data.outputs || [];
        if (data.status === 'failed') throw new Error(data.error || 'Preview failed.');
      }
      throw new Error('This is taking longer than usual — please try again in a moment.');
    }

    setRoomCompare(value) {
      const next = Math.max(0, Math.min(100, Number(value) || 0));
      this.state.roomCompare = next;
      const comparison = this.querySelector('[data-room-comparison]');
      const divider = this.querySelector('[data-room-compare]');
      if (comparison) comparison.style.setProperty('--ds-room-split', `${next}%`);
      if (divider) divider.setAttribute('aria-valuenow', String(Math.round(next)));
    }

    onRoomComparePointerDown(event) {
      const comparison = event.currentTarget.closest('[data-room-comparison]');
      if (!comparison) return;
      const update = (pointerEvent) => {
        const rect = comparison.getBoundingClientRect();
        this.setRoomCompare(((pointerEvent.clientX - rect.left) / Math.max(1, rect.width)) * 100);
      };
      const stop = () => {
        window.removeEventListener('pointermove', update);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
      };
      event.preventDefault();
      update(event);
      window.addEventListener('pointermove', update);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    }

    onRoomCompareKeyDown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.setRoomCompare(0);
      else if (event.key === 'End') this.setRoomCompare(100);
      else this.setRoomCompare((this.state.roomCompare || 50) + (event.key === 'ArrowRight' ? 2 : -2));
    }

    parseQuad(str) {
      if (!str) return null;
      const nums = String(str).split(/[\s,]+/).map(Number).filter((n) => !Number.isNaN(n));
      if (nums.length < 8) return null;
      return [
        { x: nums[0], y: nums[1] },
        { x: nums[2], y: nums[3] },
        { x: nums[4], y: nums[5] },
        { x: nums[6], y: nums[7] },
      ];
    }

    /** Persisted AI generation counter (24h rolling window, per browser). */
    readPersistedAiUsage() {
      try {
        const raw = localStorage.getItem(this.aiUsageStorageKey);
        if (!raw) return 0;
        const data = JSON.parse(raw);
        if (!data || typeof data.count !== 'number' || typeof data.firstAt !== 'number') return 0;
        if (Date.now() - data.firstAt > this.aiUsageWindowMs) {
          localStorage.removeItem(this.aiUsageStorageKey);
          return 0;
        }
        return Math.max(0, data.count);
      } catch { return 0; }
    }

    writePersistedAiUsage(count) {
      try {
        const existing = JSON.parse(localStorage.getItem(this.aiUsageStorageKey) || '{}');
        const firstAt = (existing && typeof existing.firstAt === 'number' && Date.now() - existing.firstAt < this.aiUsageWindowMs)
          ? existing.firstAt
          : Date.now();
        localStorage.setItem(this.aiUsageStorageKey, JSON.stringify({ count, firstAt }));
      } catch {}
    }

    readAiHistory() {
      try {
        const raw = localStorage.getItem(this.aiHistoryStorageKey);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter((design) => design && design.imageUrl) : [];
      } catch { return []; }
    }

    writeAiHistory(list) {
      try {
        localStorage.setItem(this.aiHistoryStorageKey, JSON.stringify((list || []).slice(0, this.aiHistoryMax)));
      } catch {}
    }

    rememberAiDesigns(urls, generatedPrompt) {
      const prompt = String(generatedPrompt || '').trim();
      const existing = this.state.aiHistory || [];
      const seen = new Set(existing.map((design) => design.imageUrl));
      const additions = [];
      (urls || []).forEach((url) => {
        if (!url || seen.has(url)) return;
        seen.add(url);
        additions.push({
          id: 'aih:' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
          imageUrl: url,
          prompt,
          createdAt: Date.now(),
        });
      });
      if (!additions.length) return;
      const merged = [...additions, ...existing].slice(0, this.aiHistoryMax);
      this.state.aiHistory = merged;
      this.writeAiHistory(merged);
      additions.forEach((design) => {
        fetch(`${this.proxy}/saved-designs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ imageUrl: design.imageUrl, prompt: design.prompt || undefined, source: 'ai' }),
        }).catch(() => {});
      });
    }

    async loadAiHistoryFromServer() {
      try {
        const response = await fetch(`${this.proxy}/saved-designs`, { credentials: 'same-origin' });
        if (!response.ok) return;
        const data = await response.json().catch(() => ({}));
        const rows = Array.isArray(data.designs) ? data.designs : [];
        const existing = this.state.aiHistory || [];
        const seen = new Set(existing.map((design) => design.imageUrl));
        const fromServer = rows
          .filter((design) => design && design.imageUrl && design.source === 'ai' && !seen.has(design.imageUrl))
          .map((design) => ({
            id: 'ais:' + design.id,
            imageUrl: design.imageUrl,
            prompt: design.prompt || '',
            createdAt: design.createdAt ? Date.parse(design.createdAt) : Date.now(),
          }));
        const merged = [...existing, ...fromServer]
          .sort((first, second) => (second.createdAt || 0) - (first.createdAt || 0))
          .slice(0, this.aiHistoryMax);
        this.state.aiHistory = merged;
        this.writeAiHistory(merged);
        if (this.state.sourceTab === 'ai') this.render();
      } catch {}
    }

    /** Choose a preset room — load it, apply saved quad. */
    selectPresetRoom(url, quadStr) {
      const quad = this.parseQuad(quadStr) || [
        { x: 0, y: 0 }, { x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 },
      ];
      this.setState({
        roomUrl: url,
        roomPublicUrl: null,
        roomSource: 'preset',
        roomQuad: quad,
        roomFitUrl: null,
        roomFitWallpaperUrl: null,
        previewError: null,
        bwMode: false,
      });
    }

    /** Compute quad-to-quad projective transform (Heckbert) and apply as matrix3d. */
    applyPerspective() {
      const stage = this.querySelector('[data-stage]');
      const img = this.querySelector('[data-room-img]');
      const wall = this.querySelector('[data-wall]');
      if (!stage || !img || !wall) return;

      const quadPct = this.state.roomQuad;
      if (!quadPct || quadPct.length !== 4) { wall.style.display = 'none'; return; }
      wall.style.display = '';

      const r = img.getBoundingClientRect();
      const s = stage.getBoundingClientRect();
      const ox = r.left - s.left;
      const oy = r.top - s.top;
      const W = r.width, H = r.height;
      if (W < 10 || H < 10) return;

      const q = quadPct.map((p) => ({ x: ox + (p.x / 100) * W, y: oy + (p.y / 100) * H }));
      const m = quadToQuadMatrix3d(W, H, q[0], q[1], q[2], q[3]);

      wall.style.width = W + 'px';
      wall.style.height = H + 'px';
      wall.style.left = '0px';
      wall.style.top = '0px';
      wall.style.transformOrigin = '0 0';
      wall.style.transform = m;

      // Position handles
      const handlesWrap = this.querySelector('[data-handles]');
      if (handlesWrap) {
        const corners = handlesWrap.querySelectorAll('.ds-handle:not(.ds-handle--center)');
        corners.forEach((h, i) => {
          const p = quadPct[i];
          h.style.left = `${ox + (p.x / 100) * W}px`;
          h.style.top = `${oy + (p.y / 100) * H}px`;
        });
        const center = handlesWrap.querySelector('.ds-handle--center');
        if (center) {
          const cx = quadPct.reduce((a, p) => a + p.x, 0) / 4;
          const cy = quadPct.reduce((a, p) => a + p.y, 0) / 4;
          center.style.left = `${ox + (cx / 100) * W}px`;
          center.style.top = `${oy + (cy / 100) * H}px`;
        }
      }
    }

    /** Pointer-drag for corner/center handles. */
    onHandlePointerDown(e) {
      const idx = e.currentTarget.dataset.handleIdx;
      e.preventDefault();
      const img = this.querySelector('[data-room-img]');
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const startQuad = this.state.roomQuad.map((p) => ({ ...p }));
      const startX = e.clientX, startY = e.clientY;
      const W = rect.width, H = rect.height;

      const move = (ev) => {
        const dxPct = ((ev.clientX - startX) / W) * 100;
        const dyPct = ((ev.clientY - startY) / H) * 100;
        let next;
        if (idx === 'c') {
          next = startQuad.map((p) => ({
            x: Math.max(0, Math.min(100, p.x + dxPct)),
            y: Math.max(0, Math.min(100, p.y + dyPct)),
          }));
        } else {
          const i = Number(idx);
          next = startQuad.map((p, j) => j === i
            ? { x: Math.max(0, Math.min(100, p.x + dxPct)), y: Math.max(0, Math.min(100, p.y + dyPct)) }
            : p);
        }
        this.state.roomQuad = next;
        this.applyPerspective();
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }

    /* ---------------- Crop interactions ---------------- */
    cropAspect() {
      const d = this.state.dims;
      return (d.width || 1) / (d.height || 1);
    }

    /** Ensure crop is sane: positive, inside [0,100], aspect-locked to wall. */
    clampCrop() {
      const img = this.querySelector('[data-crop-img]');
      const stage = this.querySelector('[data-crop-stage]');
      if (!img || !stage) return;
      const r = img.getBoundingClientRect();
      if (r.width < 10 || r.height < 10) return;
      const imgPxAspect = r.width / r.height;
      const wallAspect = this.cropAspect(); // w/h in cm
      // Convert wall aspect (w/h, cm) to % aspect within image pixel box.
      // We want crop.w% / crop.h%  such that  (crop.w * r.width) / (crop.h * r.height) = wallAspect
      // → crop.w / crop.h = wallAspect * r.height / r.width = wallAspect / imgPxAspect
      const ratioPct = wallAspect / imgPxAspect;
      let crop = this.state.crop || this.defaultCrop();
      // Reapply ratio: keep height, recompute width.
      let h = Math.min(100, Math.max(2, crop.h));
      let w = Math.min(100, h * ratioPct);
      if (w > 100) { w = 100; h = w / ratioPct; }
      const x = Math.max(0, Math.min(100 - w, crop.x));
      const y = Math.max(0, Math.min(100 - h, crop.y));
      this.state.crop = { x, y, w, h };
      const el = this.querySelector('[data-crop-rect]');
      if (el) {
        el.style.left = `${x}%`; el.style.top = `${y}%`;
        el.style.width = `${w}%`; el.style.height = `${h}%`;
      }
    }

    onCropDrag(e) {
      if (e.target.closest('[data-crop-handle]')) return;
      e.preventDefault();
      const img = this.querySelector('[data-crop-img]');
      if (!img) return;
      const r = img.getBoundingClientRect();
      const startX = e.clientX, startY = e.clientY;
      const start = { ...(this.state.crop || this.defaultCrop()) };
      const move = (ev) => {
        const dxPct = ((ev.clientX - startX) / r.width) * 100;
        const dyPct = ((ev.clientY - startY) / r.height) * 100;
        const x = Math.max(0, Math.min(100 - start.w, start.x + dxPct));
        const y = Math.max(0, Math.min(100 - start.h, start.y + dyPct));
        this.state.crop = { ...start, x, y };
        const el = this.querySelector('[data-crop-rect]');
        if (el) { el.style.left = `${x}%`; el.style.top = `${y}%`; }
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }

    onCropHandle(e) {
      e.preventDefault();
      e.stopPropagation();
      const corner = e.currentTarget.dataset.cropHandle; // nw|ne|se|sw
      const img = this.querySelector('[data-crop-img]');
      if (!img) return;
      const r = img.getBoundingClientRect();
      const imgPxAspect = r.width / r.height;
      const ratioPct = this.cropAspect() / imgPxAspect; // crop.w/crop.h target
      const startX = e.clientX, startY = e.clientY;
      const start = { ...(this.state.crop || this.defaultCrop()) };
      // Anchor opposite corner stays fixed.
      const anchor = {
        x: corner.includes('w') ? start.x + start.w : start.x,
        y: corner.includes('n') ? start.y + start.h : start.y,
      };
      const move = (ev) => {
        const dxPct = ((ev.clientX - startX) / r.width) * 100;
        const dyPct = ((ev.clientY - startY) / r.height) * 100;
        // Move the dragged corner.
        let cx = (corner.includes('w') ? start.x : start.x + start.w) + dxPct;
        let cy = (corner.includes('n') ? start.y : start.y + start.h) + dyPct;
        // Clamp inside [0,100].
        cx = Math.max(0, Math.min(100, cx));
        cy = Math.max(0, Math.min(100, cy));
        let w = Math.abs(anchor.x - cx);
        let h = Math.abs(anchor.y - cy);
        // Lock to ratio (h derived from w via ratioPct).
        const proposedW = h * ratioPct;
        if (proposedW <= w) { h = w / ratioPct; } else { w = proposedW; }
        // Re-clamp so we don't exceed bounds.
        const newX = corner.includes('w') ? Math.max(0, anchor.x - w) : anchor.x;
        const newY = corner.includes('n') ? Math.max(0, anchor.y - h) : anchor.y;
        const maxW = corner.includes('w') ? anchor.x : 100 - anchor.x;
        const maxH = corner.includes('n') ? anchor.y : 100 - anchor.y;
        if (w > maxW) { w = maxW; h = w / ratioPct; }
        if (h > maxH) { h = maxH; w = h * ratioPct; }
        const x = corner.includes('w') ? anchor.x - w : anchor.x;
        const y = corner.includes('n') ? anchor.y - h : anchor.y;
        if (w < 5 || h < 5) return;
        this.state.crop = { x, y, w, h };
        const el = this.querySelector('[data-crop-rect]');
        if (el) {
          el.style.left = `${x}%`; el.style.top = `${y}%`;
          el.style.width = `${w}%`; el.style.height = `${h}%`;
        }
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }

    /* ---------------- Review pan/zoom ---------------- */
    /** Initial fit: scale image so it COVERS the canvas (no empty edges). */
    fitFinalImage() {
      const frame = this.querySelector('[data-final-frame]');
      const img = this.querySelector('[data-final-img]');
      if (!frame || !img || !img.naturalWidth) return;
      const fr = frame.getBoundingClientRect();
      if (fr.width < 10 || fr.height < 10) return;
      // Cover the CUT FRAME (not the whole canvas) so the design at minimum zoom
      // exactly fills the printable area, leaving the customer free to pan the
      // image's edges up to the frame edge (free-wheel positioning).
      const coverScale = Math.max(fr.width / img.naturalWidth, fr.height / img.naturalHeight);
      this._minScale = coverScale;
      this.state.imgTransform = { tx: 0, ty: 0, scale: coverScale };
      this.applyFinalTransform();
    }

    applyFinalTransform() {
      const img = this.querySelector('[data-final-img]');
      if (!img) return;
      const tr = this.state.imgTransform;
      const bw = !!this.state.bwMode;
      const flip = !!this.state.flip;
      img.style.transform = `translate(-50%,-50%) translate(${tr.tx}px,${tr.ty}px) scale(${tr.scale}) ${flip ? 'scaleX(-1)' : ''}`;
      img.style.filter = bw ? 'grayscale(1)' : '';
    }

    /** Cover-clamp: keep the image covering the CUT FRAME (no empty edges in the print). */
    clampFinalTransform() {
      const canvas = this.querySelector('[data-final-canvas]');
      const frame = this.querySelector('[data-final-frame]');
      const img = this.querySelector('[data-final-img]');
      if (!canvas || !frame || !img || !img.naturalWidth) return;
      const fr = frame.getBoundingClientRect();
      const tr = this.state.imgTransform;
      // Lower bound: image must at least COVER the cut frame.
      const coverScale = Math.max(fr.width / img.naturalWidth, fr.height / img.naturalHeight);
      this._minScale = coverScale;
      if (tr.scale < coverScale) tr.scale = coverScale;
      const dispW = img.naturalWidth * tr.scale;
      const dispH = img.naturalHeight * tr.scale;
      // The frame is centred in the canvas and tx/ty move the image about that
      // same centre, so allow panning until the image edge meets the frame edge.
      // This lets the customer position ANY part of the design inside the cut.
      const maxTx = Math.max(0, (dispW - fr.width) / 2);
      const maxTy = Math.max(0, (dispH - fr.height) / 2);
      tr.tx = Math.max(-maxTx, Math.min(maxTx, tr.tx));
      tr.ty = Math.max(-maxTy, Math.min(maxTy, tr.ty));
    }

    onFinalImgDrag(e) {
      e.preventDefault();
      const startX = e.clientX, startY = e.clientY;
      const start = { ...this.state.imgTransform };
      const move = (ev) => {
        this.state.imgTransform = {
          ...start,
          tx: start.tx + (ev.clientX - startX),
          ty: start.ty + (ev.clientY - startY),
        };
        this.clampFinalTransform();
        this.applyFinalTransform();
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }

    /** Step zoom by a factor relative to current scale (min = cover, max = 4× cover). */
    stepZoom(factor) {
      const cur = this.state.imgTransform.scale || 1;
      const min = this._minScale || 1;
      const max = min * 4;
      const next = Math.max(min, Math.min(max, cur * factor));
      this.state.imgTransform = { ...this.state.imgTransform, scale: next };
      this.clampFinalTransform();
      this.applyFinalTransform();
    }

    /** Convert current image transform + frame position to a crop rect in % of natural image. */
    computeCropFromTransform() {
      const canvas = this.querySelector('[data-final-canvas]');
      const frame = this.querySelector('[data-final-frame]');
      const img = this.querySelector('[data-final-img]');
      if (!canvas || !frame || !img || !img.naturalWidth) return null;
      const cr = canvas.getBoundingClientRect();
      const fr = frame.getBoundingClientRect();
      const tr = this.state.imgTransform;
      const dispW = img.naturalWidth * tr.scale;
      const dispH = img.naturalHeight * tr.scale;
      const imgCx = cr.width / 2 + tr.tx;
      const imgCy = cr.height / 2 + tr.ty;
      const imgLeft = imgCx - dispW / 2;
      const imgTop = imgCy - dispH / 2;
      const frLeft = fr.left - cr.left;
      const frTop = fr.top - cr.top;
      const relX = frLeft - imgLeft;
      const relY = frTop - imgTop;
      const w = (fr.width / dispW) * 100;
      const h = (fr.height / dispH) * 100;
      const x = (relX / dispW) * 100;
      const y = (relY / dispH) * 100;
      return {
        x: Math.max(0, Math.min(100 - Math.max(1, Math.min(100, w)), x)),
        y: Math.max(0, Math.min(100 - Math.max(1, Math.min(100, h)), y)),
        w: Math.max(1, Math.min(100, w)),
        h: Math.max(1, Math.min(100, h)),
      };
    }

    /**
     * Poll the storefront product JSON until a freshly-minted variant has
     * propagated into Shopify's storefront index and is purchasable. Returns
     * true once the variant is visible + available; returns false if the window
     * elapses first (the add-retry loop is then the fallback). Never throws.
     *
     * The variant is created via the Admin API (instant there) but /cart/add.js
     * reads the eventually-consistent storefront index — polling the product
     * .js (a GET that never 422s) lets us wait for that gap to close WITHOUT
     * burning a visible failed add attempt.
     */
    async waitForVariantReady(variantId, { maxWaitMs = 30000, handle } = {}) {
      const productHandle = handle || this.sharedProductHandle || 'pickawall-custom-wallpaper';
      const wanted = Number(variantId);
      if (!wanted) return false;
      const deadline = Date.now() + maxWaitMs;
      let delay = 700;
      let unreadable = 0; // consecutive responses we couldn't parse as product JSON
      while (Date.now() < deadline) {
        try {
          const resp = await fetch(`/products/${productHandle}.js?_=${Date.now()}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            cache: 'no-store',
          });
          const data = resp.ok ? await resp.json().catch(() => null) : null;
          if (data && Array.isArray(data.variants)) {
            unreadable = 0;
            const match = data.variants.find((v) => Number(v.id) === wanted);
            if (match && match.available) return true;
          } else {
            // Not a usable product JSON (e.g. the storefront password page, a
            // redirect, or a rate-limit HTML body). This endpoint can't tell us
            // anything — bail quickly rather than burning the whole window, and
            // let the add-retry loop handle propagation instead.
            unreadable += 1;
            if (unreadable >= 2) return false;
          }
        } catch { /* network blip — keep polling until the deadline */ }
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = Math.min(delay + 300, 2000); // gentle backoff, capped
      }
      return false;
    }

    async addToCart() {
      if (document.documentElement.getAttribute('data-pw-geo-blocked') === 'true') {
        this.setState({ cartError: 'We currently take custom-wallpaper orders inside Australia only. Please send us an enquiry so we can quote international shipping and lead time.' });
        const dlg = document.getElementById('pw-geo-dialog');
        if (dlg) { try { if (dlg.showModal && !dlg.open) dlg.showModal(); else dlg.setAttribute('open', ''); } catch (_) { dlg.setAttribute('open', ''); } }
        return;
      }
      const d = this.state.dims;
      const choice = this.state.chosen[0];
      const safeProp = (v) => {
        const s = String(v || '');
        if (!s) return '';
        if (s.startsWith('blob:')) return '';
        return s.length > 250 ? s.slice(0, 250) : s;
      };

      if (!choice) {
        this.setState({ cartError: 'Please select a wallpaper first.' });
        return;
      }

      // Minimum order size. The Next button and the CTA are already disabled
      // below the threshold, but dimensions can also arrive prefilled from the
      // URL (?w=&h=&step=4), which skips those gates entirely. The backend
      // rejects undersized walls too; this is the friendly message.
      const sizeCheck = this.calc();
      if (sizeCheck.belowMinimum) {
        this.setState({
          step: 2,
          cartError: `This wall is ${sizeCheck.wallAreaM2.toFixed(2)} m². The minimum order is ${this.minOrderM2.toFixed(2)} m² — please increase the width or height.`,
        });
        return;
      }
      if (this.pricePerM2 <= 0) {
        this.setState({ cartError: 'Price per m² is not configured. Theme settings → Pickawall → set "Price per m²".' });
        return;
      }
      if (!d.width || !d.height) {
        this.setState({ cartError: 'Please enter your wall width and height in Step 1.' });
        return;
      }

      // Native Shopify cart flow:
      //   1. Render the final cropped cut (server-side, so the merchant gets
      //      the file even before payment).
      //   2. Mint a hidden per-order product (POST /cart-item) priced at the
      //      computed m² total, with the cropped preview as its image. The
      //      server computes the price so the client can't tamper with it.
      //   3. Add the returned variant to the regular Shopify cart via
      //      /cart/add.js with line-item properties, then open the theme's
      //      native cart drawer.
      this.setState({ cartError: null, cartLoading: true, cartStage: 'crop' });
      try {
        const colourMode = this.state.bwMode ? 'bw' : 'colour';
        const widthCm = Number(d.width);
        const heightCm = Number(d.height);
        const wastagePct = this.state.wastageOn ? (Number(d.wastage) || 0) : 0;
        const designUrl = safeProp(choice.imageUrl);
        const roomUrl = safeProp(this.state.roomUrl);
        const source = choice.source === 'ai' ? 'design-studio:ai' : 'design-studio:collection';

        // Step 1: render the final cut (non-fatal if it fails).
        const liveCrop = this.computeCropFromTransform();
        if (liveCrop) this.state.crop = liveCrop;

        // Signature of everything that determines the minted variant (its price
        // and image). If it is unchanged from a previous add attempt in this
        // session we reuse the already-created variant instead of minting a
        // brand-new one. This (a) stops duplicate variants piling up in the
        // admin on every "tap again", and (b) lets the retry succeed using a
        // variant that has now had time to propagate to the storefront — a
        // freshly-minted variant is the sole cause of the transient 422
        // "sold out", so reusing an older one sidesteps it entirely.
        const variantSig = JSON.stringify({
          img: choice.imageUrl || '',
          crop: this.state.crop || null,
          flip: !!this.state.flip,
          colourMode,
          widthCm,
          heightCm,
          wastagePct,
          pricePerM2: this.pricePerM2,
        });

        let cutUrl;
        let cutPreviewUrl;
        let ci;
        const cachedVariant = this._lastVariant;
        if (cachedVariant && cachedVariant.sig === variantSig && cachedVariant.variantId) {
          // Reuse the previously-created variant + rendered cut — it has had
          // time to propagate, and we avoid minting a duplicate admin variant.
          ci = { variantId: cachedVariant.variantId, productId: cachedVariant.productId, productHandle: cachedVariant.productHandle };
          cutUrl = cachedVariant.cutUrl;
          cutPreviewUrl = cachedVariant.cutPreviewUrl;
        } else if (choice.imageUrl && this.state.crop) {
          try {
            const rr = await fetch(`${this.proxy}/render-cut`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({
                designUrl: choice.imageUrl,
                crop: this.state.crop,
                flip: !!this.state.flip,
                colourMode,
                widthCm,
                heightCm,
              }),
            });
            const rb = await rr.json().catch(() => ({}));
            if (rr.ok && rb.cutUrl) {
              cutUrl = rb.cutUrl;
              cutPreviewUrl = rb.cutPreviewUrl || rb.cutUrl;
            }
          } catch { /* non-fatal */ }
        }
        const finalCut = cutUrl || (designUrl || '');
        const finalCutPreview = cutPreviewUrl || finalCut;

        const productTitle = choice.title
          || (choice.source === 'ai' ? 'AI Generated Wallpaper' : 'Custom Wallpaper');

        const sourceProductUrl = choice.url
          || (choice.handle && (choice.source === 'collection' || choice.source === 'gallery')
              ? `${window.location.origin}/products/${choice.handle}`
              : '');

        // Step 2: mint the hidden per-order product. The server computes the
        // price from the dimensions, so the variant carries the correct m²
        // total — the only way to get a custom price into the native cart on
        // a non-Plus store. The cropped preview becomes the product image, so
        // the cart/checkout/order thumbnail shows the wallpaper natively.
        this.setCartStage('create');
        if (!ci) {
          const ciResp = await fetch(`${this.proxy}/cart-item`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              widthCm,
              heightCm,
              wastagePct,
              pricePerM2: this.pricePerM2,
              currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD',
              title: productTitle,
              source,
              prompt: choice.prompt || undefined,
              designUrl: designUrl || undefined,
              roomUrl: roomUrl || undefined,
              // Use the lightweight preview as the product image (the full print
              // PNG can be 50-100 MB and breaks image upload/thumbnails).
              cutUrl: finalCutPreview || undefined,
              sourceProductUrl: sourceProductUrl || undefined,
              colourMode,
            }),
          });
          ci = await ciResp.json().catch(() => ({}));
          if (!ciResp.ok || !ci.variantId) {
            throw new Error(ci.error || `Could not create cart item (${ciResp.status}).`);
          }
          // Remember this variant so a subsequent "tap again" (after storefront
          // propagation lag) reuses it instead of minting yet another admin
          // variant — the reused variant has had time to become purchasable.
          this._lastVariant = {
            sig: variantSig,
            variantId: ci.variantId,
            productId: ci.productId,
            productHandle: ci.productHandle,
            cutUrl,
            cutPreviewUrl,
          };
        }

        // Step 3: build line-item properties. Keys prefixed with `_` are
        // hidden from the customer; the rest show in the cart and checkout.
        const properties = {
          'Wall width': `${Math.round(widthCm)} cm`,
          'Wall height': `${Math.round(heightCm)} cm`,
          'Wall area': `${((widthCm * heightCm) / 10000).toFixed(2)} m²`,
          'Wastage': `${wastagePct}%`,
          'Colour mode': colourMode === 'bw' ? 'Black & white' : 'Colour',
        };
        if (choice.prompt) properties['Prompt'] = String(choice.prompt).slice(0, 250);
        if (finalCutPreview) {
          // Hidden — used for the cart thumbnail, order confirmation and fulfilment.
          // Not shown as a visible "Cropped print" detail line in the cart.
          properties['_final_cut'] = finalCutPreview;
        }
        if (cutUrl && cutUrl !== finalCutPreview) properties['_print_file'] = cutUrl;
        if (sourceProductUrl && (choice.source === 'collection' || choice.source === 'gallery')) {
          // Hidden (underscore) — used by line-item.liquid to link the title to
          // the source product, but not shown as its own raw-URL cart line.
          properties['_source_product_url'] = sourceProductUrl;
        }
        if (designUrl) properties['_design_url'] = designUrl;
        properties['_source'] = source;

        // Add to the native Shopify cart, requesting the cart-drawer section
        // markup back so the theme can re-render the drawer in place.
        this.setCartStage('add');
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }));
        // A freshly-created variant (or a brand-new shared product) can take a
        // moment to become purchasable on the storefront (Shopify propagation
        // lag), surfacing as a 422 "sold out" / "not available" error. Retry
        // with backoff so the first add of a brand-new design succeeds instead
        // of failing the customer. A brand-new *product* propagates slower than
        // a new variant on an existing product, so we allow a generous window.
        let addResp;
        let addJson;
        // A freshly-created variant is untracked + continue-selling (verified
        // server-side), so a 422 "sold out" here is ALWAYS transient storefront
        // propagation lag — never a real stock issue. Retry patiently; the
        // processing overlay keeps the customer informed while we wait.
        //
        // We poll `/cart/add.js`, which is storefront-rate-limited: hitting it
        // too fast returns 429 (an HTML error page, not JSON). So we must
        // (a) NOT hammer it, and (b) treat 429 as retriable, not fatal.
        //
        // Each per-order product is isolated, so its variant becomes purchasable
        // within ~1s and the pre-warm poll below almost always makes the first
        // add succeed. This short retry is only a safety net for the residual
        // propagation edge; it intentionally does NOT hammer /cart/add.js (that
        // would self-inflict the 429 throttle that makes a transient 422 stick).
        const maxAddAttempts = 8;
        let sawPropagationLag = false;
        // Instead of a blind fixed delay, actively poll the storefront product
        // JSON until the freshly-minted variant is visible AND purchasable, then
        // fire the first add. This turns the old visible "422 → tap again" into a
        // silent wait behind the processing overlay. Skipped when reusing an
        // already-propagated cached variant. The add-retry loop below remains as
        // a safety net if propagation somehow outlasts the poll window.
        if (!(cachedVariant && cachedVariant.sig === variantSig)) {
          await this.waitForVariantReady(ci.variantId, { handle: ci.productHandle });
        }
        for (let attempt = 1; attempt <= maxAddAttempts; attempt += 1) {
          addResp = await fetch(this.addToCartUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [{ id: ci.variantId, quantity: 1, properties }],
              sections: sectionsToBundle.join(','),
              sections_url: window.location.pathname,
            }),
          });
          addJson = await addResp.json().catch(() => ({}));
          if (addResp.ok) break;
          // Shopify's /cart/add.js reports failures inconsistently: sometimes
          // `description`/`message`, sometimes a plain `errors` string
          // ("Cart Error"), sometimes an `errors` object of field messages.
          // Normalise all of them so detection + the surfaced message work.
          let errText = addJson.description || addJson.message || '';
          if (!errText && addJson.errors) {
            errText = typeof addJson.errors === 'string'
              ? addJson.errors
              : Object.values(addJson.errors).flat().join(' ');
          }
          // Our custom variants are always untracked + continue-selling
          // (enforced server-side), so a 422 here is NEVER a real stock issue —
          // it is storefront propagation lag, and Shopify may report it either
          // as "sold out"/"not available" OR as the opaque generic "Cart Error"
          // (with no description). Treat EVERY 422 as retriable propagation lag.
          const propagationLag = addResp.status === 422;
          const rateLimited = addResp.status === 429; // storefront throttle (HTML body)
          const serverBlip = addResp.status >= 500; // transient upstream hiccup
          const retriable = propagationLag || rateLimited || serverBlip;
          if (propagationLag) sawPropagationLag = true;
          if (!retriable || attempt === maxAddAttempts) {
            // Attach the HTTP status + raw detail so the catch block can build a
            // structured, customer-friendly message (generic headline + detail,
            // plus a contact link for server-side 5xx errors).
            const rawDetail = errText || '';
            const e = new Error(rawDetail || `Add to cart failed (${addResp.status}).`);
            e.status = addResp.status;
            e.propagationLag = sawPropagationLag || rateLimited;
            throw e;
          }
          // Gentle backoff: long enough to (a) let the storefront index catch
          // up and (b) stay under the /cart/add.js rate limit. A 429 means we
          // are being throttled, so wait noticeably longer before retrying.
          const wait = rateLimited ? 4000 : Math.min(1500 + 500 * attempt, 3000);
          await new Promise((resolve) => setTimeout(resolve, wait));
        }

        // Success — take the customer straight to the cart page. We keep the
        // overlay up (cartLoading stays true) through the navigation so there's
        // no flash of the design studio between "added" and the cart page.
        this.setCartStage('done');
        this.bumpHeaderCart();
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        window.location.assign(`${root.replace(/\/$/, '')}/cart`);
      } catch (err) {
        this.setState({ cartLoading: false, cartError: this.buildCartError(err) });
      }
    }

    /** Build a structured, customer-friendly cart error from a thrown error. */
    buildCartError(err) {
      const status = err && err.status;
      const rawDetail = (err && err.message) || 'Could not add to cart.';
      if (status >= 500) {
        return {
          title: 'Add to cart error, please contact the administrator.',
          detail: `Error ${status}: ${rawDetail}`,
          contact: true,
        };
      }
      if (status) {
        // 422 (incl. propagation lag / throttle) and other client-side failures.
        const detail = err && err.propagationLag
          ? `Error ${status}: your custom size is still being prepared. Please tap “Add to cart” again in a moment.`
          : `Error ${status}: ${rawDetail}`;
        return {
          title: 'Something went wrong. Please try to add to cart again.',
          detail,
          contact: false,
        };
      }
      // Validation / non-HTTP errors (no status) — show the message as-is.
      return { title: rawDetail, detail: '', contact: false };
    }

    /** Render the cart error block (supports plain strings and structured objects). */
    renderCartError() {
      const e = this.state.cartError;
      if (!e) return '';
      const err = (typeof e === 'string') ? { title: e } : e;
      const contactLink = err.contact
        ? ` <a class="ds-cart-error__contact" href="${this.contactUrl}">Contact us</a>`
        : '';
      return `
        <div class="ds-error ds-cart-error" role="alert">
          <p class="ds-cart-error__title">${this.escapeHtml(err.title)}${contactLink}</p>
          ${err.detail ? `<p class="ds-cart-error__detail">${this.escapeHtml(err.detail)}</p>` : ''}
        </div>`;
    }

    /** Find the header cart icon's screen position and pulse the badge. */
    bumpHeaderCart() {
      const anchor = document.querySelector('.header__cart-link a, .header__cart-link') || document.querySelector('a[href*="/cart"]');
      if (anchor) {
        const r = anchor.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        document.documentElement.style.setProperty('--ds-cart-anchor-x', `${cx}px`);
        document.documentElement.style.setProperty('--ds-cart-anchor-y', `${cy}px`);
        anchor.classList.add('ds-cart-bumped');
        setTimeout(() => anchor.classList.remove('ds-cart-bumped'), 600);
      }
      // Best-effort badge count: increment Prestige's <cart-count> tag.
      document.querySelectorAll('cart-count').forEach((el) => {
        const n = Number(el.textContent || '0') || 0;
        el.textContent = String(n + 1);
      });
      document.querySelectorAll('cart-dot').forEach((el) => el.classList.add('is-visible'));
    }
  }

  if (!customElements.get('design-studio')) customElements.define('design-studio', DesignStudio);
})();
